import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import {
  TextField,
  Toggle,
  Button,
  LoadingOverlay,
  DatePicker,
} from '@finpay-development/shared-components';
import {
  AttachMoney as AttachMoneyIcon,
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  MenuItem,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { formatNumberToUSD } from '../../shared/calculators';
import { 
  getPatientsAndChampionsList, 
  PAYMENT_SOURCE_TYPES,
  NEW_PAYOR_ID,
  getDateInFuture,
  getValidRecurringDate,
  filterBankAccounts,
  filterCardMethods,
  getDefaultCardMethodAndCustomerId,
  formatPaymentMethodDisplay,
  formatExpiration,
  createPaymentNote,
  triggerCrmUpdate,
  parseAmount,
  formatCurrencyInput,
  stripCurrencyInput,
  createPaymentHubValidationSchema,
  getGuarantorAddressDetails,
  getSuccessRoute,
  deleteAndRefetchPaymentMethods,
} from '../hub-utils';
import PortalLoginEmailField from './portal-login-email-field';
import PaymentHubCardForm, { CardPaymentRef } from './payment-hub-card-form';
import PaymentHubACHForm, { ACHPaymentRef } from './payment-hub-ach-form';
import RecurringPaymentMethodSection, { RecurringPaymentMethodSectionRef } from './recurring-payment-method-section';
import PaymentMethodList from './payment-method-list';
import PaymentSourceTabs from './payment-source-tabs';
import { DeletePaymentMethodWarningModal } from './delete-payment-method-warning-modal';
import { 
  savePaymentMethod, 
  createACHPaymentMethod, 
  fetchUserPaymentMethods,
} from '../services/payment-method-service';
import { createPayment, createSetupIntent } from '../services/payment-service';
import { createProgram, updateProgramSchedule, fetchProgram } from '../services/program-service';
import { patientService } from '../../patient/services/patient-service';
import { workFlowStatus } from '../../patient/components/models/workflow-status';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import { setSelectedEncounter, setSelectedPatientV2 } from '../../patient/state/patient-slice';
import { addPatientToRecentPatientsIfNeeded } from '../../patient/state/patient-thunk';
import { StripePaymentMethod } from '../../patient/components/models/stripe-payment-method';
import { showErrorStatus, showStatus } from '../../security/state/user-slice';
import { Program, PAYMENT_CHANNELS, PAYMENT_TYPE, UserPaymentMethod, PaymentMethodType, ScheduleRequest, PaymentMethod } from '@finpay-payment-v3/payment-types';
import { userService } from '../../admin/services/admin-user-service';
import { ApiBatchLimits } from '../../shared/enums';
import { executeEnroll } from '../services/enroll-service';
import { createChampionIfNeeded, updatePayorInfo } from '../services/champion-service';
import { isPaidInFull as checkIfPaidInFull, resolveGuarantorForEnrollment, buildUpdatePayorInfoParams, resolveRecurringPaymentMethod } from '../hub-utils';
import { PayorWithUserStatus } from '../interfaces/payment-hub-interfaces';
import { validatePreEnrollRequirements, PreEnrollRequirement } from '../utils/pre-enroll-validation';
import PreEnrollErrorModal from './pre-enroll-error-modal';
import { validatePrePayRequirements, PrePayRequirement } from '../utils/pre-pay-validation';
import PrePayErrorModal from './pre-pay-error-modal';
import { CHANNEL_CODE_TO_ID, ChannelCode } from '../state/payment-context-slice';
import GuarantorAddressFields from './guarantor-address-fields';
import { useHubChannel } from '../hooks/use-hub-channel';
import { Utils } from '../../shared/utils';

import '../../scss/components/_btn-group.scss';


interface UserLookupState {
  isChecking: boolean;
  isChecked: boolean;
  userExists: boolean;
  userId: string | null;
  stripeCustomerId: string | null;
  savedPaymentMethods: UserPaymentMethod[];
}

const PaymentHubView = () => {
  const { patientId, patientEncounterId } = useParams<{ patientId: string; patientEncounterId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedPatient = useSelector((state: RootState) => state.patientContext.selectedPatient);
  const selectedEncounter = useSelector((state: RootState) => state.patientContext.selectedEncounter);
  
  const isConverted = selectedEncounter?.workflow?.workflowId === 3;

  const allClients = useSelector((state: RootState) => 
    state.implementationContext?.implementationSpecialistClient?.allClients || []
  );


  const [isPatientLoading, setIsPatientLoading] = useState(false);
  const [isEncounterLoading, setIsEncounterLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);

  const [userLookupState, setUserLookupState] = useState<UserLookupState>({
    isChecking: false,
    isChecked: false,
    userExists: false,
    userId: null,
    stripeCustomerId: null,
    savedPaymentMethods: [],
  });

  const [payorsWithStatus, setPayorsWithStatus] = useState<PayorWithUserStatus[]>([]);
  const [isPreCheckingPayors, setIsPreCheckingPayors] = useState(false);

  const [selectedSavedMethod, setSelectedSavedMethod] = useState<string | null>(null);

  const [paymentSource, setPaymentSource] = useState<keyof typeof PAYMENT_SOURCE_TYPES>('CARD');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAmountDisabled, setIsAmountDisabled] = useState(false);
  const [isZeroDownPayment, setIsZeroDownPayment] = useState(false);
  const [isDownPaymentFullyPaid, setIsDownPaymentFullyPaid] = useState(false);
  const [isFullPayProgram, setIsFullPayProgram] = useState(false);

  // Pre-enroll validation modal state
  const [showPreEnrollErrorModal, setShowPreEnrollErrorModal] = useState(false);
  const [preEnrollFailedRequirements, setPreEnrollFailedRequirements] = useState<PreEnrollRequirement[]>([]);

  // Pre-pay validation modal state (for non-enroll flows)
  const [showPrePayErrorModal, setShowPrePayErrorModal] = useState(false);
  const [prePayFailedRequirements, setPrePayFailedRequirements] = useState<PrePayRequirement[]>([]);

  const [isAddingNewPayor, setIsAddingNewPayor] = useState(false);
  const [newPayorFirstName, setNewPayorFirstName] = useState('');
  const [newPayorLastName, setNewPayorLastName] = useState('');
  const [newPayorEmail, setNewPayorEmail] = useState('');
  const [isLookingUpNewPayor, setIsLookingUpNewPayor] = useState(false);
  const emailLookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [billingDetails, setBillingDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [addressDetails, setAddressDetails] = useState({
    streetAddress1: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    birthDate: '',
    relationshipToPatient: '',
  });


  


  // Validation state for Cash/Check billing fields
  const [cashCheckTouched, setCashCheckTouched] = useState({ firstName: false, lastName: false, email: false });

  const handleCashCheckBlur = (field: 'firstName' | 'lastName' | 'email') => {
    setCashCheckTouched(prev => ({ ...prev, [field]: true }));
  };

  const getCashCheckError = (field: 'firstName' | 'lastName' | 'email'): string | null => {
    if (!isAddingNewPayor && field !== 'email') return null;
    if (!cashCheckTouched[field]) return null;
    if (!billingDetails[field]?.trim()) {
      const labels = { firstName: 'First name', lastName: 'Last name', email: 'Email' };
      return `${labels[field]} is required`;
    }
    if (field === 'email' && billingDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingDetails.email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const [isDownPaymentGuarantorCheckboxChecked, setIsDownPaymentGuarantorCheckboxChecked] = useState(false);

  // State for recurring payment payor 
  const [recurringPayorId, setRecurringPayorId] = useState<string>('');
  const [isRecurringGuarantorCheckboxChecked, setIsRecurringGuarantorCheckboxChecked] = useState(false);
  const [isAddingNewRecurringPayor, setIsAddingNewRecurringPayor] = useState(false);
  const [newRecurringPayorFirstName, setNewRecurringPayorFirstName] = useState('');
  const [newRecurringPayorLastName, setNewRecurringPayorLastName] = useState('');
  
  // User lookup state for recurring payor 
  const [recurringUserLookupState, setRecurringUserLookupState] = useState<UserLookupState>({
    isChecking: false,
    isChecked: false,
    userExists: false,
    userId: null,
    stripeCustomerId: null,
    savedPaymentMethods: [],
  });
  const [recurringBillingDetails, setRecurringBillingDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [recurringAddressDetails, setRecurringAddressDetails] = useState({
    streetAddress1: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    birthDate: '',
    relationshipToPatient: '',
  });


  // Read isProgramSetup, programId, and isPaidInFull from location.state
  const isProgramSetup = (location.state as { isProgramSetup?: boolean })?.isProgramSetup ?? false;
  const programIdFromState = (location.state as { programId?: number })?.programId;
  const isPaidInFull = (location.state as { isPaidInFull?: boolean })?.isPaidInFull ?? false;

  // State for fetched program details from v3 API
  const [fetchedProgram, setFetchedProgram] = useState<any>(null);
  const [isFetchingProgram, setIsFetchingProgram] = useState(false);

  // Legacy: program object (for backward compatibility, will be removed)
  const program = (location.state as { program?: Program })?.program;
  const isMobile = location.pathname.startsWith('/mobile');
  const authToken = useSelector((state: RootState) => state?.userContext?.userProfile?.tokenState?.jwtIdToken);
  
  // Set channel from URL or default to fms (also handles /mobile routes)
  const { currentChannelId: channelIdFromContext } = useHubChannel();
  
  // Also check URL directly for ?ch= param (in case navigating directly to /pay)
  const channelFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const ch = searchParams.get('ch') as ChannelCode | null;
    if (ch && ['web', 'vt', 'crm', 'mobile', 'fms'].includes(ch)) {
      return CHANNEL_CODE_TO_ID[ch];
    }
    return null;
  }, [location.search]);
  
  // Determine payment channel: use context, then URL, then fall back to URL-based detection
  const paymentChannelId = channelIdFromContext 
    ?? channelFromUrl
    ?? (isMobile ? PAYMENT_CHANNELS.MOBILE : PAYMENT_CHANNELS.MANAGED_SERVICE);

  const cardPaymentRef = useRef<CardPaymentRef>(null);
  const achPaymentRef = useRef<ACHPaymentRef>(null);

  // State for SetupIntent (created on load without extCustomerId, used for adding new payment methods)
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [isSetupIntentLoading, setIsSetupIntentLoading] = useState(false);

  // State for ACH saved method selection
  const [selectedACHMethodId, setSelectedACHMethodId] = useState<number | null>(null);

  // State for tracking which payment method is being deleted
  const [deletingMethodId, setDeletingMethodId] = useState<number | null>(null);

  // State for recurring payment method
  const [recurringPaymentMethodId, setRecurringPaymentMethodId] = useState<number | null>(null);
  const [showRecurringMethodModal, setShowRecurringMethodModal] = useState(false);
  const [pendingDeleteMethodId, setPendingDeleteMethodId] = useState<number | null>(null);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [warningPendingDeleteMethodId, setWarningPendingDeleteMethodId] = useState<number | null>(null);
  const [warningDeleteSource, setWarningDeleteSource] = useState<'downPayment' | 'recurring'>('downPayment');
  
  // Ref for recurring payment method section
  const recurringPaymentMethodSectionRef = useRef<RecurringPaymentMethodSectionRef>(null);

  const handleErrorAndRedirect = (errorMessage: string) => {
    dispatch(showErrorStatus(errorMessage));
    navigate('/hub');
  };

  useEffect(() => {

    if (!patientId || !patientEncounterId) {
      handleErrorAndRedirect('Patient ID and Encounter ID are required.');
      return;
    }

    const loadPatientEncounterAndPayors = async () => {

      setIsPatientLoading(true);
      setIsEncounterLoading(true);

      try {
        const [patientRes, encounterRes] = await Promise.all([
          patientService.getPatient(parseInt(patientId)),
          patientService.getPatientInstanceOfCare({
            patientId: parseInt(patientId),
            encounterId: parseInt(patientEncounterId),
          }),
        ]);

        if (patientRes.hasErrors || !patientRes.entity) {
          handleErrorAndRedirect('Patient not found.');
          return;
        }
        if (encounterRes.hasErrors || !encounterRes.entity) {
          handleErrorAndRedirect('Encounter not found.');
          return;
        }
        if (encounterRes.entity.patientId !== parseInt(patientId)) {
          handleErrorAndRedirect('Encounter does not belong to this patient.');
          return;
        }

        const patient = patientRes.entity;
        const encounter = encounterRes.entity;

        dispatch(setSelectedPatientV2(patient));
        dispatch(setSelectedEncounter(encounter));
        dispatch(addPatientToRecentPatientsIfNeeded(parseInt(patientId)));

        const payorsList = await getPatientsAndChampionsList(patient, encounter, false, authToken);
        setPayorsWithStatus(payorsList as PayorWithUserStatus[]);

      } catch (error) {
        console.error('Error loading patient data:', error);
        handleErrorAndRedirect('Failed to load patient data.');
      } finally {
        setIsPatientLoading(false);
        setIsEncounterLoading(false);
      }
    };

    loadPatientEncounterAndPayors();

  }, [])


  // Fetch program details from v3 API when programIdFromState is provided
  useEffect(() => {
    if (!programIdFromState || !authToken) {
      return;
    }

    const fetchProgramDetails = async () => {
      setIsFetchingProgram(true);
      try {
        const result = await fetchProgram({ programId: programIdFromState, authToken });

        if (result.success && result.program) {
          setFetchedProgram(result.program);
          
          const programData = result.program;
          const downPmtAmt = programData.downPmtAmt || 0;
          const schedule = programData.patientPaymentSchedule as (ScheduleRequest & { pfrPendingBalance?: number }) | undefined;
          const pfrAmt = schedule?.pfrAmt || 0;
          const pfrPendingBalance = schedule?.pfrPendingBalance || 0;
          
          // Calculate how much has already been paid toward the PFR
          const amountAlreadyPaid = pfrAmt - pfrPendingBalance;
          
          // Remaining down payment = total down payment required minus what's already been paid
          const remainingDownPayment = Math.max(0, downPmtAmt - amountAlreadyPaid);
          
          // The amount due is the lesser of remaining down payment or pending balance
          const amountDue = Math.min(remainingDownPayment, pfrPendingBalance);
          
          console.log('[PaymentHub] Pre-fill amount calculation:', {
            downPmtAmt,
            pfrAmt,
            pfrPendingBalance,
            amountAlreadyPaid,
            remainingDownPayment,
            amountDue,
          });
          
          // Check if this is a full pay program (paymentFreq === 'F')
          const isFullPay = schedule?.paymentFreq === 'F';
          
          if (isFullPay) {
            setIsFullPayProgram(true);
          }
          
          // Check if this is a zero down payment program
          if (downPmtAmt === 0) {
            setIsZeroDownPayment(true);
            setIsAmountDisabled(true);
            formik.setFieldValue('amount', '0.00', true);
            console.log('[PaymentHub] Zero down payment program detected - hiding payment section');
          } else if (amountDue <= 0) {
            // Down payment is fully paid
            setIsDownPaymentFullyPaid(true);
            setIsAmountDisabled(true);
            formik.setFieldValue('amount', '0.00', true);
            console.log('[PaymentHub] Down payment fully paid - hiding payment section');
          } else if (isFullPay) {
            // Full pay program - disable amount editing, pre-fill with amount due
            setIsAmountDisabled(true);
            formik.setFieldValue('amount', formatCurrencyInput(amountDue), true);
          } else {
            // Program setup flow - amount is always disabled (pre-calculated down payment)
            setIsAmountDisabled(true);
            formik.setFieldValue('amount', formatCurrencyInput(amountDue), true);
          }
        }
      } catch (error: any) {
        console.error('Error fetching program details:', error);
      } finally {
        setIsFetchingProgram(false);
      }
    };

    fetchProgramDetails();
  }, [programIdFromState, authToken]);

  // Fetch v3 program from encounter's programId when no programIdFromState was provided
  useEffect(() => {
    if (programIdFromState || fetchedProgram || !authToken) return;
    const encounterProgramId = selectedEncounter?.patientPaymentProgram?.[0]?.patientPaymentProgramId;
    if (!encounterProgramId) return;

    (async () => {
      try {
        const result = await fetchProgram({ programId: encounterProgramId, authToken });
        if (result.success && result.program) {
          setFetchedProgram(result.program);
        }
      } catch (error) {
        console.error('[PaymentHub] Error fetching v3 program:', error);
      }
    })();
  }, [programIdFromState, fetchedProgram, authToken, selectedEncounter?.patientPaymentProgram]);

  // Ref to prevent double SetupIntent creation (React Strict Mode calls useEffect twice in dev)
  const setupIntentCreatedRef = useRef(false);

  // Create SetupIntent on component load (without extCustomerId)
  // This will be used to render the Payment Element when "Add new payment method" is selected
  useEffect(() => {
    const initSetupIntent = async () => {
      // Only create if we haven't already started (ref prevents Strict Mode double-call)
      if (setupIntentCreatedRef.current) {
        console.log('[PaymentHub] SetupIntent already created, skipping...');
        return;
      }
      setupIntentCreatedRef.current = true;

      setIsSetupIntentLoading(true);
      try {
        console.log('[PaymentHub] Creating SetupIntent on load (no customer)...');
        const result = await createSetupIntent({});
        
        if (result.success && result.clientSecret && result.setupIntentId) {
          setSetupIntentClientSecret(result.clientSecret);
          setSetupIntentId(result.setupIntentId);
          console.log('[PaymentHub] SetupIntent created:', result.setupIntentId);
        } else {
          console.error('[PaymentHub] Failed to create SetupIntent:', result.message);
        }
      } catch (error) {
        console.error('[PaymentHub] Error creating SetupIntent:', error);
      } finally {
        setIsSetupIntentLoading(false);
      }
    };

    initSetupIntent();
  }, []); // Run once on mount

  const lookupUserByEmail = useCallback(async (email: string) => {
    if (!email) {
      setUserLookupState({
        isChecking: false,
        isChecked: false,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
      setSelectedSavedMethod(null);
      return;
    }

    setUserLookupState((prev) => ({ ...prev, isChecking: true }));

    try {
      // Lookup user by email using user service API
      const userResponse = await userService.getAllUsers(0, ApiBatchLimits.users, { userName: email });
      const userRecord = userResponse?.entity?.[0];

      let savedPaymentMethods: UserPaymentMethod[] = [];
      let stripeCustomerId: string | null = null;

      if (userRecord && userRecord.userId) {
        const pmResult = await fetchUserPaymentMethods({ userId: userRecord.userId, authToken });
        if (pmResult.success) {
          savedPaymentMethods = pmResult.paymentMethods;
        }
      }

      // Get default card method and Stripe customer ID
      const { defaultCardMethodId, stripeCustomerId: customerId } = getDefaultCardMethodAndCustomerId(savedPaymentMethods);
      stripeCustomerId = customerId;

      setUserLookupState({
        isChecking: false,
        isChecked: true,
        userExists: !!userRecord,
        userId: userRecord?.userId?.toString() || null,
        stripeCustomerId,
        savedPaymentMethods,
      });

      setSelectedSavedMethod(defaultCardMethodId);
    } catch (error) {
      console.error('Error looking up user:', error);
      setUserLookupState({
        isChecking: false,
        isChecked: true,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
    }
  }, [authToken]);

  // Refetch payment methods for the current user and create a new SetupIntent
  const refetchPaymentMethods = useCallback(async () => {
    if (!userLookupState.userId) {
      return;
    }

    try {
      // Fetch payment methods using service
      const result = await fetchUserPaymentMethods({
        userId: parseInt(userLookupState.userId, 10),
        authToken,
      });

      if (result.success) {
        const savedPaymentMethods = result.paymentMethods;
        
        // Update state with new payment methods
        setUserLookupState(prev => ({
          ...prev,
          savedPaymentMethods,
        }));

        console.log('Payment methods refetched:', savedPaymentMethods.length);

        // Set recurring payment method to first Mastercard if not already set
        if (!recurringPaymentMethodId) {
          const cardMethods = filterCardMethods(savedPaymentMethods);
          const firstMastercard = cardMethods.find(
            m => m.cardBrand?.toLowerCase() === 'mastercard'
          );
          if (firstMastercard?.userPaymentMethodId) {
            setRecurringPaymentMethodId(firstMastercard.userPaymentMethodId);
          }
        }
      }

      // Create a new SetupIntent for future payment method additions
      console.log('[PaymentHub] Creating new SetupIntent after refetch...');
      const setupResult = await createSetupIntent({});
      if (setupResult.success && setupResult.clientSecret && setupResult.setupIntentId) {
        setSetupIntentClientSecret(setupResult.clientSecret);
        setSetupIntentId(setupResult.setupIntentId);
        console.log('[PaymentHub] New SetupIntent created:', setupResult.setupIntentId);
      } else {
        console.error('[PaymentHub] Failed to create new SetupIntent:', setupResult.message);
      }
    } catch (error) {
      console.error('Error refetching payment methods:', error);
    }
  }, [userLookupState.userId, authToken, recurringPaymentMethodId]);

  const schedulePaymentFreq = fetchedProgram?.patientPaymentSchedule?.paymentFreq ?? null;
  const scheduleRecurringMethodId = fetchedProgram?.patientPaymentSchedule?.recurringUserPaymentMethodId ?? null;
  const isActiveRecurringProgram = (schedulePaymentFreq === 'M' || schedulePaymentFreq === 'W') && !fetchedProgram?.isPaidInFull;

  const executeRecurringDelete = useCallback(async (methodId: number) => {
    if (!recurringUserLookupState.userId || deletingMethodId) return;
    setDeletingMethodId(methodId);
    try {
      const result = await deleteAndRefetchPaymentMethods({
        userId: parseInt(recurringUserLookupState.userId, 10),
        userPaymentMethodId: methodId,
        authToken,
      });
      if (result.success) {
        setRecurringUserLookupState(prev => ({
          ...prev,
          savedPaymentMethods: result.paymentMethods,
        }));
      } else {
        dispatch(showErrorStatus(result.message));
      }
    } finally {
      setDeletingMethodId(null);
    }
  }, [recurringUserLookupState.userId, deletingMethodId, authToken, dispatch]);

  const handleDeleteRecurringMethod = useCallback(async (methodId: number) => {
    if (isActiveRecurringProgram && methodId === scheduleRecurringMethodId) {
      setWarningPendingDeleteMethodId(methodId);
      setWarningDeleteSource('recurring');
      setShowDeleteWarningModal(true);
    } else {
      await executeRecurringDelete(methodId);
    }
  }, [isActiveRecurringProgram, scheduleRecurringMethodId, executeRecurringDelete]);

  const executeDownPaymentDelete = useCallback(async (methodId: number) => {
    if (!userLookupState.userId || deletingMethodId) return;
    setDeletingMethodId(methodId);
    try {
      const result = await deleteAndRefetchPaymentMethods({
        userId: parseInt(userLookupState.userId, 10),
        userPaymentMethodId: methodId,
        authToken,
      });
      if (result.success) {
        setUserLookupState(prev => ({ ...prev, savedPaymentMethods: result.paymentMethods }));
        if (selectedSavedMethod === methodId.toString()) {
          setSelectedSavedMethod('new');
        }
      } else {
        dispatch(showErrorStatus(result.message));
      }
    } finally {
      setDeletingMethodId(null);
    }
  }, [userLookupState.userId, deletingMethodId, authToken, dispatch, selectedSavedMethod]);

  const handleDeleteDownPaymentMethod = useCallback(async (methodId: number) => {
    if (isActiveRecurringProgram && methodId === scheduleRecurringMethodId) {
      setWarningPendingDeleteMethodId(methodId);
      setWarningDeleteSource('downPayment');
      setShowDeleteWarningModal(true);
    } else {
      await executeDownPaymentDelete(methodId);
    }
  }, [isActiveRecurringProgram, scheduleRecurringMethodId, executeDownPaymentDelete]);

  const handleConfirmWarningDelete = useCallback(async () => {
    setShowDeleteWarningModal(false);
    if (warningPendingDeleteMethodId) {
      if (warningDeleteSource === 'recurring') {
        await executeRecurringDelete(warningPendingDeleteMethodId);
      } else {
        await executeDownPaymentDelete(warningPendingDeleteMethodId);
      }
      setWarningPendingDeleteMethodId(null);
    }
  }, [warningPendingDeleteMethodId, warningDeleteSource, executeRecurringDelete, executeDownPaymentDelete]);

  // Set recurring payment method to first Mastercard when payment methods change
  useEffect(() => {
    if (recurringPaymentMethodId) return; // Already set
    
    const cardMethods = filterCardMethods(userLookupState.savedPaymentMethods);
    const firstMastercard = cardMethods.find(
      m => m.cardBrand?.toLowerCase() === 'mastercard'
    );
    if (firstMastercard?.userPaymentMethodId) {
      setRecurringPaymentMethodId(firstMastercard.userPaymentMethodId);
    }
  }, [userLookupState.savedPaymentMethods, recurringPaymentMethodId]);

  const handleNewPayorEmailChange = useCallback((email: string) => {
    setNewPayorEmail(email);
    
    if (emailLookupTimeoutRef.current) {
      clearTimeout(emailLookupTimeoutRef.current);
    }
    
    if (!email || !email.includes('@')) {
      setUserLookupState({
        isChecking: false,
        isChecked: false,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
      setSelectedSavedMethod(null);
      return;
    }
    
    setIsLookingUpNewPayor(true);
    emailLookupTimeoutRef.current = setTimeout(async () => {
      // Use the same lookupUserByEmail function
      await lookupUserByEmail(email);
      setIsLookingUpNewPayor(false);
    }, 500);
  }, [lookupUserByEmail]);

  useEffect(() => {
    return () => {
      if (emailLookupTimeoutRef.current) {
        clearTimeout(emailLookupTimeoutRef.current);
      }
    };
  }, []);

  const patientName = `${selectedPatient.contact?.firstName || ''} ${selectedPatient.contact?.lastName || ''}`.trim() || 'Loading...';

  const paymentProgram = selectedEncounter.patientPaymentProgram?.[0];
  
  const pendingBalance = fetchedProgram?.patientPaymentSchedule?.pfrPendingBalance
    ?? paymentProgram?.patientPaymentSchedule?.pfrPendingBalance
    ?? (isFetchingProgram ? undefined : selectedEncounter.pfrAmt);


  const initialFormValues = useMemo(() => ({
    payorName: '',
    receiptEmail: '',
    amount: '',
    paymentReason: '',
    payAtFacility: false,
    payAtFacilityAmount: '',
    useExistingSource: false,
    paymentSources: '',
    useForRecurring: true,
    recurringPaymentDate: getValidRecurringDate().toISOString(),
  }), []);

  // Create validation schema based on payment source and enrollment-only state
  // When down payment is fully paid or zero down payment, we don't require payment amount
  const isEnrollmentOnlyValidation = isProgramSetup && (isDownPaymentFullyPaid || isZeroDownPayment);
  const validationSchema = useMemo(() => createPaymentHubValidationSchema(paymentSource, isEnrollmentOnlyValidation), [paymentSource, isEnrollmentOnlyValidation]);

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    enableReinitialize: false,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      console.log('[PaymentHub] Form submitted, values:', values);
      console.log('[PaymentHub] isProgramSetup:', isProgramSetup);
      setIsProcessing(true);

      try {
        // Run pre-enroll validation for enrollment flows
        if (isProgramSetup) {
          console.log('[PaymentHub] Running pre-enroll validation...');
          const paymentAmount = parseAmount(values.amount) || 0;
          const programForValidation = fetchedProgram || program;
          const pendingBalance = programForValidation?.patientPaymentSchedule?.pfrPendingBalance;

          // Determine if creating new guarantor
          const isCreatingNewGuarantor = isDownPaymentGuarantorCheckboxChecked || isRecurringGuarantorCheckboxChecked;

          // Determine existing guarantor status
          const thirdPartyGuarantor = selectedEncounter?.patientChampion?.filter(
            (champion) => champion.isGuarantor
          ) || [];
          const hasExistingGuarantor = thirdPartyGuarantor.length > 0;

          // Determine which guarantor form is visible (same logic as onSubmit/button click)
          const selectedDownPaymentPayor = payorsWithStatus.find(p => p.id?.toString() === values.payorName);
          const selectedRecurringPayorSubmit = payorsWithStatus.find(p => p.id?.toString() === recurringPayorId);
          
          const isDownPaymentGuarantorFormVisible = !isZeroDownPayment && !isDownPaymentFullyPaid && 
            ((isProgramSetup && (values.useForRecurring || isFullPayProgram)) || !isProgramSetup) &&
            !isRecurringGuarantorCheckboxChecked && (isDownPaymentGuarantorCheckboxChecked || selectedDownPaymentPayor?.isGuarantor);
          const isRecurringGuarantorFormVisible = !isDownPaymentGuarantorCheckboxChecked && 
            (isRecurringGuarantorCheckboxChecked || selectedRecurringPayorSubmit?.isGuarantor);

          // Use form data from whichever guarantor form is currently visible
          const visibleFormData = isDownPaymentGuarantorFormVisible ? addressDetails
            : (isRecurringGuarantorFormVisible ? recurringAddressDetails : null);

          const guarantorFormData = visibleFormData ? {
            phone: visibleFormData.phone,
            birthDate: visibleFormData.birthDate,
            relationshipToPatient: visibleFormData.relationshipToPatient,
            address: {
              streetAddress1: visibleFormData.streetAddress1,
              city: visibleFormData.city,
              state: visibleFormData.state,
              zipCode: visibleFormData.zipCode,
            },
          } : undefined;

          // Determine account holder name
          let accountHolderName: string | undefined;
          if (isCreatingNewGuarantor) {
            const isNewDownPaymentPayor = isDownPaymentGuarantorCheckboxChecked && values.payorName === NEW_PAYOR_ID;
            const isNewRecurringPayor = isRecurringGuarantorCheckboxChecked && recurringPayorId === NEW_PAYOR_ID;
            
            if (isNewDownPaymentPayor) {
              accountHolderName = `${newPayorFirstName} ${newPayorLastName}`.trim() || 'New Guarantor';
            } else if (isNewRecurringPayor) {
              accountHolderName = `${newRecurringPayorFirstName} ${newRecurringPayorLastName}`.trim() || 'New Guarantor';
            } else {
              const selectedPayorId = isDownPaymentGuarantorCheckboxChecked ? values.payorName : recurringPayorId;
              const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === selectedPayorId);
              accountHolderName = selectedPayor?.name || 'Account Holder';
            }
          } else if (hasExistingGuarantor) {
            accountHolderName = `${thirdPartyGuarantor[0]?.contact?.firstName || ''} ${thirdPartyGuarantor[0]?.contact?.lastName || ''}`.trim();
          } else {
            accountHolderName = `${selectedPatient?.contact?.firstName || ''} ${selectedPatient?.contact?.lastName || ''}`.trim();
          }

          const useForRecurringApplies = values.useForRecurring && !isZeroDownPayment && !isDownPaymentFullyPaid;
          const hasRecurringMethodSubmit = useForRecurringApplies
            || !!(recurringPaymentMethodSectionRef.current?.getSelectedMethodId())
            || !!(recurringPaymentMethodSectionRef.current?.isAddingNew());

          const needsDownPaymentACH = paymentSource === 'ACH' && !isZeroDownPayment && !isDownPaymentFullyPaid;
          const isDownPaymentACHValid = !needsDownPaymentACH || !achPaymentRef.current || achPaymentRef.current.validate();

          const recurringRef = recurringPaymentMethodSectionRef.current;
          const needsRecurringACH = recurringRef?.isAddingNew() && recurringRef?.getPaymentSource() === 'ACH';
          const isRecurringACHValid = !needsRecurringACH || !recurringRef?.getAchFormRef() || recurringRef.getAchFormRef()!.validate();

          const isACHValid = isDownPaymentACHValid && isRecurringACHValid;

          const validationResult = validatePreEnrollRequirements({
            encounter: selectedEncounter,
            patient: selectedPatient,
            program: programForValidation,
            isDownPaymentFullyPaid,
            isLoading: false,
            hasRecurringMethod: hasRecurringMethodSubmit,
            currentPaymentAmount: paymentAmount,
            pendingRemainingBalance: pendingBalance,
            recurringPaymentDate: values.recurringPaymentDate,
            isCreatingNewGuarantor,
            newGuarantorData: isCreatingNewGuarantor ? guarantorFormData : undefined,
            existingGuarantorFormData: !isCreatingNewGuarantor ? guarantorFormData : undefined,
            accountHolderName,
            patientId: patientId ? parseInt(patientId, 10) : undefined,
            encounterId: patientEncounterId ? parseInt(patientEncounterId, 10) : undefined,
            billingEmail: (isZeroDownPayment || isDownPaymentFullyPaid) ? recurringBillingDetails.email : billingDetails.email,
            isPaymentMethodValid: isACHValid,
          });

          if (!validationResult.isValid) {
            setPreEnrollFailedRequirements(validationResult.failedRequirements);
            setShowPreEnrollErrorModal(true);
            setIsProcessing(false);
            return;
          }
        } else {
          // For non-enrollment flow, pass guarantor form data if the guarantor form is visible
          // This includes: existing guarantors OR when "make guarantor" checkbox is checked
          const selectedPayorForValidation = payorsWithStatus.find(p => p.id?.toString() === values.payorName);
          const isGuarantorFormVisible = selectedPayorForValidation?.isGuarantor || isDownPaymentGuarantorCheckboxChecked;
          const guarantorFormDataForValidation = isGuarantorFormVisible ? {
            birthDate: addressDetails.birthDate,
          } : undefined;

          const prePayValidationResult = validatePrePayRequirements({
            patient: selectedPatient,
            patientId: patientId ? parseInt(patientId, 10) : undefined,
            billingEmail: billingDetails.email,
            guarantorFormData: guarantorFormDataForValidation,
            isProgramSetup,
            downPaymentChampion: {
              isNewChampion: values.payorName === NEW_PAYOR_ID,
              isGuarantor: isDownPaymentGuarantorCheckboxChecked,
              addressDetails,
              label: 'down payment champion',
            },
            recurringChampion: {
              isNewChampion: recurringPayorId === NEW_PAYOR_ID,
              isGuarantor: isRecurringGuarantorCheckboxChecked,
              addressDetails: recurringAddressDetails,
              label: 'recurring payment champion',
            },
          });

          if (!prePayValidationResult.isValid) {
            setPrePayFailedRequirements(prePayValidationResult.failedRequirements);
            setShowPrePayErrorModal(true);
            setIsProcessing(false);
            return;
          }
        }

        // create any new champions before processing payment
        let selectedPayorId: string | number = values.payorName;
        let iocData: any = null;
        let userPaymentMethodId: number | undefined;
        let newChampionUserId: number | undefined;
        let newRecurringChampionUserId: number | undefined;

        const championDeps = {
          dispatch,
          selectedPatient,
          authToken,
          onPayorsListUpdate: setPayorsWithStatus,
        };

        if (values.payorName === NEW_PAYOR_ID) {
          const result = await createChampionIfNeeded(
            {
              payorId: values.payorName,
              billingDetails,
              isGuarantor: isDownPaymentGuarantorCheckboxChecked,
              isRequired: true,
              patientId: patientId!,
              patientEncounterId: patientEncounterId!,
              clientId: selectedEncounter.clientId,
              address: isDownPaymentGuarantorCheckboxChecked ? addressDetails : undefined,
            },
            championDeps,
            (error) => dispatch(showErrorStatus(error))
          );

          if (!result.success) {
            setIsProcessing(false);
            return;
          }

          selectedPayorId = result.championId!;
          newChampionUserId = result.userId;
          formik.setFieldValue('payorName', selectedPayorId.toString(), false);

          // Refresh iocData for later use
          const iocResponse = await patientService.getPatientInstanceOfCare({
            patientId: parseInt(patientId!, 10),
            encounterId: parseInt(patientEncounterId!, 10),
          });
          if (!iocResponse.hasErrors && iocResponse.entity) {
            iocData = iocResponse.entity;
          }

          console.log('[PaymentHub] Down payment champion created:', result.championId);
        }

        // Create recurring champion if needed
        if (recurringPayorId === NEW_PAYOR_ID) {
          const result = await createChampionIfNeeded(
            {
              payorId: recurringPayorId,
              billingDetails: recurringBillingDetails,
              isGuarantor: isRecurringGuarantorCheckboxChecked,
              isRequired: false,
              patientId: patientId!,
              patientEncounterId: patientEncounterId!,
              clientId: selectedEncounter.clientId,
              address: isRecurringGuarantorCheckboxChecked ? recurringAddressDetails : undefined,
            },
            championDeps
          );

          if (result.success) {
            newRecurringChampionUserId = result.userId;
          }
        }

        // Get program ID - priority: 1) from location.state, 2) from IOC response, 3) create skeleton program
        let programId: number | undefined = programIdFromState;

        if (!programId) {
          // Fetch encounter data to check for existing program
          if (!iocData && patientId && patientEncounterId) {
            const iocResponse = await patientService.getPatientInstanceOfCare({
              patientId: parseInt(patientId, 10),
              encounterId: parseInt(patientEncounterId, 10),
            });

            if (iocResponse.hasErrors) {
              dispatch(showErrorStatus(iocResponse.errorMessage || 'Failed to fetch encounter data'));
              setIsProcessing(false);
              return;
            }

            iocData = iocResponse.entity;
            console.log('IOC data fetched:', iocData);
          }

          // Check for patientPaymentProgram in IOC response
          if (iocData?.patientPaymentProgram && Array.isArray(iocData.patientPaymentProgram) && iocData.patientPaymentProgram.length > 0) {
            // Program exists in encounter response - get the program ID
            const existingProgram = iocData.patientPaymentProgram[0];
            programId = existingProgram.patientPaymentProgramId;
            console.log('Found existing program ID in encounter response:', programId);
          }
        }

        // If still no program, create a skeleton program
        if (!programId) {
          console.log('[PaymentHub] No program exists, creating skeleton program...');
          
          // Use iocData if available, otherwise use selectedEncounter from Redux
          const encounter = iocData || selectedEncounter;
          if (!encounter || !encounter.clientId || !encounter.facilityId) {
            dispatch(showErrorStatus('Unable to create program: missing encounter data'));
            setIsProcessing(false);
            return;
          }
          
          const pfrAmt = encounter.adjustedPfrAmt ?? encounter.pfrAmt ?? 0;
          const paymentAmount = parseAmount(values.amount) || pfrAmt;
          
          // Create skeleton schedule using ScheduleRequest type from @finpay-payment-v3/payment-types
          const skeletonSchedule: ScheduleRequest = {
            paymentFreq: 'M',
            pfrAmt: pfrAmt,
            pfrBalance: pfrAmt,
            terms: 0,
            remainingTerms: 0,
            paymentDueAmt: 0,
          };
          
          const isAchPayment = paymentSource === 'ACH';
          const programResult = await createProgram({
            clientId: encounter.clientId,
            facilityId: encounter.facilityId,
            patientId: parseInt(patientId!, 10),
            patientEncounterId: parseInt(patientEncounterId!, 10),
            programPfrAmt: pfrAmt,
            downPmtAmt: paymentAmount,
            patientPaymentSchedule: skeletonSchedule,
            isAch: isAchPayment,
            isAchVerified: isAchPayment,
            authToken,
          });
          
          if (!programResult.success || !programResult.programId) {
            dispatch(showErrorStatus(programResult.message || 'Failed to create payment program'));
            setIsProcessing(false);
            return;
          }
          
          programId = programResult.programId;
        }

        if (!programId) {
          dispatch(showErrorStatus('Program ID is required to process payment'));
          setIsProcessing(false);
          return;
        }

        // Check if this is a facility-only payment (no direct payment, just facility record)
        // Do this early to skip unnecessary user/payment method creation
        const paymentAmount = parseAmount(values.amount);
        const facilityAmount = values.payAtFacility && values.payAtFacilityAmount 
          ? parseAmount(values.payAtFacilityAmount) 
          : 0;
        const isFacilityOnlyPayment = paymentAmount <= 0 && facilityAmount > 0 && values.payAtFacility;

        // For facility-only payments, skip user creation and payment method handling
        // Just create the facility note and update workflow status
        if (isFacilityOnlyPayment) {
          // Trigger CRM update
          if (patientId && patientEncounterId) {
            const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);
            await triggerCrmUpdate({
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              client,
            });
          }

          // Create "pay at facility" note
          if (facilityAmount > 0 && patientId && patientEncounterId) {
            console.log('[PaymentHub] Creating pay at facility note (facility-only):', { facilityAmount, patientId, patientEncounterId });
            await createPaymentNote(dispatch, {
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              amount: facilityAmount,
              paymentType: 'facility',
              showNotify: false,
            });

            // Update IOC workflow status for facility payment (only for pre-convert IOCs)
            const isConverted = selectedEncounter?.workflow?.workflowId === 3;
            
            if (!isConverted) {
              const pfrAmt = selectedEncounter?.adjustedPfrAmt ?? selectedEncounter?.pfrAmt ?? 0;
              
              let workflow;
              if (facilityAmount >= pfrAmt) {
                // Facility amount covers entire PFR -> "Follow Up Needed - Pending Payment at Facility"
                workflow = workFlowStatus.preconvert.find(wf =>
                  wf.workflowStatus.workflowStatusName === "Follow Up Needed" &&
                  wf.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
                );
                console.log('[PaymentHub] Matched: Follow Up Needed - Pending Payment at Facility');
              } else {
                // Facility amount covers part of PFR -> "New - Pending Payment at Facility"
                workflow = workFlowStatus.preconvert.find(wf =>
                  wf.workflowStatus.workflowStatusName === "New" &&
                  wf.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
                );
                console.log('[PaymentHub] Matched: New - Pending Payment at Facility');
              }
              
              console.log('[PaymentHub] Workflow to update:', workflow);
              
              if (workflow) {
                console.log('[PaymentHub] Updating IOC workflow for facility payment:', workflow);
                await patientService.saveStatus({
                  workFlow: workflow as any,
                  encounterId: parseInt(patientEncounterId, 10),
                  patientId: parseInt(patientId, 10),
                });
                // Update the selected encounter in Redux with the new workflow
                dispatch(setSelectedEncounter({
                  ...selectedEncounter,
                  workflow: workflow as any,
                }));
              } else {
                console.log('[PaymentHub] No workflow matched for facility payment');
              }
            }
          }

          // Show success toast
          dispatch(showStatus(`Recorded $${facilityAmount.toFixed(2)} to be taken at facility.`));

          setIsProcessing(false);
          navigate(`/hub/patient/${patientId}/ioc/${patientEncounterId}/`);
          return;
        }

        // Enrollment-only flow: when down payment is fully paid (or full pay completed), just run enrollment
        // No payment processing needed, just enrollment (documents, workflow, etc.)
        const isEnrollmentOnly = isProgramSetup && (isDownPaymentFullyPaid || isZeroDownPayment) && paymentAmount <= 0;
        
        if (isEnrollmentOnly && programId && patientId && patientEncounterId) {
          try {
            // Step 1: Resolve guarantor user up front — used for payment method save AND enrollment
            const enrollPayor = payorsWithStatus.find(p => p.id?.toString() === recurringPayorId);
            const guarantor = await resolveGuarantorForEnrollment({
              email: recurringBillingDetails.email || enrollPayor?.email,
              existingUserId: newRecurringChampionUserId 
                || enrollPayor?.userId 
                || (recurringUserLookupState.userId ? parseInt(recurringUserLookupState.userId, 10) : undefined),
              firstName: recurringBillingDetails.firstName || enrollPayor?.name?.split(' ')[0] || '',
              lastName: recurringBillingDetails.lastName || enrollPayor?.name?.split(' ').slice(1).join(' ') || '',
              clientId: selectedEncounter.clientId,
              patientEncounterId: parseInt(patientEncounterId, 10),
            });

            // Step 2: Update guarantor demographics (phone, DOB, address, relationship, email)
            const enrollOnlyPayorInfoParams = buildUpdatePayorInfoParams({
              isZeroDownPayment,
              isDownPaymentFullyPaid,
              isProgramSetup,
              isFullPayProgram,
              isDownPaymentGuarantorCheckboxChecked,
              isRecurringGuarantorCheckboxChecked,
              payorsWithStatus,
              recurringPayorId,
              recurringEmail: recurringBillingDetails.email,
              recurringAddressDetails,
              patientId,
            });

            await updatePayorInfo(
              enrollOnlyPayorInfoParams,
              {
                onPayorsListUpdate: setPayorsWithStatus,
                onResetGuarantorCheckbox: () => {
                  setIsDownPaymentGuarantorCheckboxChecked(false);
                  setIsRecurringGuarantorCheckboxChecked(false);
                },
              }
            );

            // Step 2b: If the recurring payor is the patient, update patient record with email (mirrors non-zero down flow)
            if (enrollPayor?.type === 'patient' && selectedPatient && guarantor.userId) {
              try {
                console.log('[PaymentHub] Zero-down: Updating patient record with userId and email');
                await patientService.updatePatient(
                  {
                    userId: guarantor.userId,
                    contact: {
                      email: recurringBillingDetails.email,
                    },
                  },
                  parseInt(patientId, 10)
                );
                console.log('[PaymentHub] Zero-down: Patient record updated with userId and email');

                dispatch(setSelectedPatientV2({
                  ...selectedPatient,
                  userId: guarantor.userId,
                }));
              } catch (updateError) {
                console.error('[PaymentHub] Zero-down: Failed to update patient with userId/email:', updateError);
              }
            }

            // Step 3: Resolve recurring payment method (existing selection or create new)
            let recurringMethodId: number | null = null;
            if (recurringPaymentMethodSectionRef.current) {
              const methodResult = await resolveRecurringPaymentMethod({
                sectionRef: recurringPaymentMethodSectionRef.current,
                userId: guarantor.userId,
                authToken,
                stripeCustomerId: recurringUserLookupState.stripeCustomerId || undefined,
              });
              if (!methodResult.success) {
                dispatch(showErrorStatus(methodResult.error || 'Failed to save recurring payment method.'));
                setIsProcessing(false);
                return;
              }
              recurringMethodId = methodResult.recurringMethodId;
            }
            
            // Step 4: Update program schedule — set active, recurring method, next payment date, ACH flags
            await updateProgramSchedule({
              programId,
              patientEncounterId: parseInt(patientEncounterId, 10),
              ...(recurringMethodId && { recurringUserPaymentMethodId: recurringMethodId }),
              ...(values.recurringPaymentDate && { nextPaymentDueDt: values.recurringPaymentDate }),
              isPaidInFull: isFullPayProgram ? true : undefined,
              isAch: false,
              isAchVerified: true,
              authToken,
            });
            
            // Step 5: Execute enrollment
            const programForEnrollment = fetchedProgram || program;
            
            if (programForEnrollment) {
              const enrollmentPaidInFull = isFullPayProgram ? true : checkIfPaidInFull(programForEnrollment);
              const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);
              
              const enrollResult = await executeEnroll({
                patientId: parseInt(patientId, 10),
                patientEncounterId: parseInt(patientEncounterId, 10),
                programId,
                isPaidInFull: enrollmentPaidInFull,
                client,
                guarantorEmail: guarantor.email,
                guarantorUserId: guarantor.userId,
              });
              
              if (enrollResult.success) {
                dispatch(showStatus('Enrollment complete.'));
              } else {
                dispatch(showErrorStatus(`Enrollment failed: ${enrollResult.message}`));
              }
            } else {
              dispatch(showErrorStatus('Unable to complete enrollment: missing program data'));
            }
          } catch (enrollError: any) {
            dispatch(showErrorStatus(`Enrollment error: ${enrollError?.message || 'An unexpected error occurred'}`));
          }
          
          setIsProcessing(false);
          navigate(getSuccessRoute(paymentChannelId, patientId));
          return;
        }

        // Ensure we have a userId for the payor - create user if needed
        // This is required for saving payment methods (not needed for facility-only payments)
        let payorUserId = newChampionUserId || (userLookupState.userId ? parseInt(userLookupState.userId, 10) : undefined);
        
        if (!payorUserId && values.payorName !== NEW_PAYOR_ID) {
          // Existing payor without a user account - check if user exists by email, create if needed
          console.log('[PaymentHub] Checking/creating user for existing payor without userId');

          try {
            // Use handleCreateOrUpdateUser which checks for existing user first (same as champion flow)
            const userResponse = await userService.handleCreateOrUpdateUser({
              userEmail: billingDetails.email,
              clientId: selectedEncounter.clientId,
              patientEncounterId: parseInt(patientEncounterId!, 10),
              name: {
                firstName: billingDetails.firstName,
                lastName: billingDetails.lastName,
              },
            });

            if (userResponse?.userId) {
              payorUserId = userResponse.userId;
              console.log('[PaymentHub] User found/created for existing payor:', payorUserId);
              
              // Update userLookupState with the new userId
              setUserLookupState(prev => ({
                ...prev,
                userId: payorUserId!.toString(),
                userExists: true,
              }));

              // If the payor is a patient, update the patient record with the userId
              const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === values.payorName);
              if (selectedPayor?.type === 'patient' && patientId && selectedPatient) {
                try {
                  console.log('[PaymentHub] Updating patient record with userId:', payorUserId);
                  // Only send userId and contact.email - the API is strict about allowed fields
                  await patientService.updatePatient(
                    {
                      userId: payorUserId,
                      contact: {
                        email: billingDetails.email,
                      },
                    },
                    parseInt(patientId, 10)
                  );
                  console.log('[PaymentHub] Patient record updated with userId');
                  
                  // Update the selectedPatient in Redux state
                  dispatch(setSelectedPatientV2({
                    ...selectedPatient,
                    userId: payorUserId,
                  }));
                } catch (updateError) {
                  // Log but don't fail - user was created, payment can still proceed
                  console.error('[PaymentHub] Failed to update patient with userId:', updateError);
                }
              }
            } else {
              dispatch(showErrorStatus('Failed to create user account for payment'));
              setIsProcessing(false);
              return;
            }
          } catch (error: any) {
            console.error('[PaymentHub] Error checking/creating user for existing payor:', error);
            dispatch(showErrorStatus(error?.message || 'Failed to create user account for payment'));
            setIsProcessing(false);
            return;
          }
        }

        let extPaymentIntentId: string | undefined;
        let newPaymentLast4 = '****'; // Track last4 for success modal

        // Note: paymentAmount, facilityAmount, and isFacilityOnlyPayment are already defined above
        // and facility-only payments have already returned early

        if (paymentSource === 'CARD') {
          if (selectedSavedMethod && selectedSavedMethod !== 'new' && userLookupState.savedPaymentMethods.length > 0) {
            // Using saved payment method
            userPaymentMethodId = parseInt(selectedSavedMethod, 10);
            if (isNaN(userPaymentMethodId)) {
              dispatch(showErrorStatus('Invalid payment method selected'));
              setIsProcessing(false);
              return;
            }
            // Get last4 from saved method
            const savedMethod = userLookupState.savedPaymentMethods.find(
              m => m.userPaymentMethodId === userPaymentMethodId
            );
            newPaymentLast4 = savedMethod?.last4 || '****';
            console.log('Paying with saved card method, userPaymentMethodId:', userPaymentMethodId);
          } else {
            // Pay with new card
            // Step 1: Create PaymentMethod in Stripe (returns full card details)
            if (!cardPaymentRef.current) {
              dispatch(showErrorStatus('Payment form not initialized'));
              setIsProcessing(false);
              return;
            }

            const cardResult = await cardPaymentRef.current.createPaymentMethod();
            console.log('Card PaymentMethod created:', cardResult);
            
            if (!cardResult.success || !cardResult.paymentMethodId) {
              dispatch(showErrorStatus(cardResult.message || 'Failed to create payment method'));
              setIsProcessing(false);
              return;
            }

            console.log('Card details from Stripe:', {
              paymentMethodId: cardResult.paymentMethodId,
              last4: cardResult.last4,
              brand: cardResult.brand,
              fingerprint: cardResult.fingerprint,
            });

            newPaymentLast4 = cardResult.last4 || '****';

            // Step 2: Save to our database (required to get userPaymentMethodId)
            // Use payorUserId which was resolved earlier (either from new champion, existing user, or newly created user)
            if (!payorUserId) {
              dispatch(showErrorStatus('User ID is required to save payment method'));
              setIsProcessing(false);
              return;
            }

            try {
              const savedMethod = await savePaymentMethod({
                userId: payorUserId,
                extPaymentMethodId: cardResult.paymentMethodId,
                // For new champions, no stripeCustomerId yet - backend will create one
                ...(!newChampionUserId && userLookupState.stripeCustomerId && { extPaymentUserId: userLookupState.stripeCustomerId }),
                paymentMethodTypeId: PaymentMethod.Card,
                last4: cardResult.last4 || '',
                cardBrand: cardResult.brand || 'card',
                expMonth: cardResult.expMonth,
                expYear: cardResult.expYear ? cardResult.expYear % 100 : undefined, // Last 2 digits only
                isVerified: true,
                authToken,
              });
              console.log('Card payment method saved to DB:', savedMethod);
              userPaymentMethodId = savedMethod.userPaymentMethodId;
            } catch (saveError: any) {
              console.error('Error saving card payment method:', saveError);
              dispatch(showErrorStatus(saveError.message || 'Failed to save payment method'));
              setIsProcessing(false);
              return;
            }
          }

        } else if (paymentSource === 'ACH') {
          // Handle ACH (bank account) payments
          if (!achPaymentRef.current) {
            dispatch(showErrorStatus('ACH payment form not initialized'));
            setIsProcessing(false);
            return;
          }

          // Check if using saved bank account
          if (achPaymentRef.current.isUsingSavedMethod()) {
            const savedMethodId = achPaymentRef.current.getSelectedSavedMethodId();
            if (savedMethodId) {
              userPaymentMethodId = savedMethodId;
              // Get last4 from saved bank accounts
              const savedBankAccounts = filterBankAccounts(userLookupState.savedPaymentMethods);
              const savedBank = savedBankAccounts.find(m => m.userPaymentMethodId === savedMethodId);
              newPaymentLast4 = savedBank?.last4 || '****';
              console.log('Paying with saved bank account, userPaymentMethodId:', userPaymentMethodId);
            } else {
              dispatch(showErrorStatus('No bank account selected'));
              setIsProcessing(false);
              return;
            }
          } else {
            // New bank account - validate form first
            if (!achPaymentRef.current.validate()) {
              dispatch(showErrorStatus('Please fill in all required bank account details'));
              setIsProcessing(false);
              return;
            }

            // Step 1: Get bank details from form
            const bankDetails = achPaymentRef.current.getBankDetails();
            const formBillingDetails = achPaymentRef.current.getBillingDetails();

            // Step 2: Create PaymentMethod with Stripe
            const pmResult = await createACHPaymentMethod({
              routingNumber: bankDetails.routingNumber,
              accountNumber: bankDetails.accountNumber,
              billingDetails: {
                name: `${formBillingDetails.firstName} ${formBillingDetails.lastName}`.trim(),
                email: formBillingDetails.email,
              },
            });

            if (!pmResult.success || !pmResult.paymentMethodId) {
              dispatch(showErrorStatus(pmResult.message || 'Failed to create bank account'));
              setIsProcessing(false);
              return;
            }

            console.log('ACH PaymentMethod created:', pmResult.paymentMethodId);
            newPaymentLast4 = pmResult.last4 || '****';

            // Step 3: Save to our database (required to get userPaymentMethodId)
            // Use payorUserId which was resolved earlier (either from new champion, existing user, or newly created user)
            if (!payorUserId) {
              dispatch(showErrorStatus('User ID is required to save payment method'));
              setIsProcessing(false);
              return;
            }

            try {
              const savedMethod = await savePaymentMethod({
                userId: payorUserId,
                extPaymentMethodId: pmResult.paymentMethodId,
                extSetupIntentId: setupIntentId || undefined,
                // For new champions, no stripeCustomerId yet - backend will create one
                ...(!newChampionUserId && userLookupState.stripeCustomerId && { extPaymentUserId: userLookupState.stripeCustomerId }),
                paymentMethodTypeId: PaymentMethod.BankAccount,
                last4: pmResult.last4 || '',
                cardBrand: 'ach',
                isVerified: true,
                authToken,
              });
              console.log('ACH payment method saved to DB:', savedMethod);
              userPaymentMethodId = savedMethod.userPaymentMethodId;
            } catch (saveError: any) {
              console.error('Error saving ACH payment method:', saveError);
              dispatch(showErrorStatus(saveError.message || 'Failed to save payment method'));
              setIsProcessing(false);
              return;
            }
          }

        } else {
          // Handle Cash or Check payments
          console.log('Processing', paymentSource, 'payment');
          
          // Create a payment method for cash/check (required by backend)
          // Use payorUserId which was resolved earlier (either from new champion, existing user, or newly created user)
          if (!payorUserId) {
            dispatch(showErrorStatus('User ID is required to process cash/check payment'));
            setIsProcessing(false);
            return;
          }

          try {
            const paymentMethodTypeId = paymentSource === 'CASH' ? PaymentMethod.Cash : PaymentMethod.Check;
            const savedMethod = await savePaymentMethod({
              userId: payorUserId,
              paymentMethodTypeId,
              isVerified: true,
              authToken,
            });
            console.log(`${paymentSource} payment method saved to DB:`, savedMethod);
            userPaymentMethodId = savedMethod.userPaymentMethodId;
          } catch (saveError: any) {
            console.error(`Error saving ${paymentSource} payment method:`, saveError);
            dispatch(showErrorStatus(saveError.message || `Failed to create ${paymentSource.toLowerCase()} payment method`));
            setIsProcessing(false);
            return;
          }
        }

        // Update contact info before payment (ensures document SMS has the phone number)
        try {
          const payorInfoParams = buildUpdatePayorInfoParams({
            isZeroDownPayment,
            isDownPaymentFullyPaid,
            isProgramSetup,
            useForRecurring: values.useForRecurring,
            isFullPayProgram,
            isDownPaymentGuarantorCheckboxChecked,
            isRecurringGuarantorCheckboxChecked,
            payorsWithStatus,
            downPaymentPayorId: values.payorName,
            downPaymentEmail: billingDetails.email,
            downPaymentAddressDetails: addressDetails,
            recurringPayorId,
            recurringEmail: recurringBillingDetails.email,
            recurringAddressDetails,
            patientId,
          });

          await updatePayorInfo(
            payorInfoParams,
            {
              onPayorsListUpdate: setPayorsWithStatus,
              onResetGuarantorCheckbox: () => {
                setIsDownPaymentGuarantorCheckboxChecked(false);
                setIsRecurringGuarantorCheckboxChecked(false);
              },
            }
          );
        } catch (contactUpdateError) {
          console.error('[PaymentHub] Failed to update contact info:', contactUpdateError);
        }

        // Create Payment for Card and ACH (electronic payments)
        // Note: Facility-only payments have already returned early, so we don't need to check here
        const isElectronicPayment = paymentSource === 'CARD' || paymentSource === 'ACH';
        if (isElectronicPayment) {
          if (!userPaymentMethodId) {
            dispatch(showErrorStatus('No payment method available'));
            setIsProcessing(false);
            return;
          }

          if (!programId) {
            dispatch(showErrorStatus('Program ID is required to process payment'));
            setIsProcessing(false);
            return;
          }

          // Determine payment type based on workflow status (converted = Enrollment)
          const paymentTypeId = isConverted ? PAYMENT_TYPE.SPECIALIST : PAYMENT_TYPE.DOWNPAYMENT;

          const paymentResult = await createPayment({
            programId,
            paymentRequest: {
              paymentAmt: paymentAmount,
              userPaymentMethodId,
              paymentChannelId,
              isAuthOnly: false,
              paymentTypeId,
              ...(values.paymentReason && { paymentReason: values.paymentReason }),
            },
          });

          if (!paymentResult.success || !paymentResult.paymentIntentId) {
            dispatch(showErrorStatus(paymentResult.message || 'Failed to process payment'));
            setIsProcessing(false);
            return;
          }

          extPaymentIntentId = paymentResult.paymentIntentId;
          console.log('[PaymentHub] Payment created:', extPaymentIntentId, 'Status:', paymentResult.status);

          // Update schedule with recurring payment method
          // If "Use for Recurring Payment" is ON, use the current payment method
          // If OFF, resolve from the recurring section (existing selection or create new)
          let recurringMethodId: number | null = null;
          if (values.useForRecurring) {
            recurringMethodId = userPaymentMethodId ?? null;
          } else if (recurringPaymentMethodSectionRef.current) {
            const isSamePayor = recurringPayorId === selectedPayorId.toString();
            const recurringUserId = newRecurringChampionUserId 
              ? newRecurringChampionUserId
              : (isSamePayor && newChampionUserId) 
                ? newChampionUserId 
                : (recurringUserLookupState.userId ? parseInt(recurringUserLookupState.userId, 10) : undefined);
            const recurringStripeCustomerId = isSamePayor 
              ? (!newChampionUserId && userLookupState.stripeCustomerId ? userLookupState.stripeCustomerId : undefined)
              : (recurringUserLookupState.stripeCustomerId || undefined);

            const methodResult = await resolveRecurringPaymentMethod({
              sectionRef: recurringPaymentMethodSectionRef.current,
              userId: recurringUserId,
              authToken,
              stripeCustomerId: recurringStripeCustomerId,
            });
            if (!methodResult.success) {
              console.error('[PaymentHub] Failed to resolve recurring payment method:', methodResult.error);
            }
            recurringMethodId = methodResult.recurringMethodId;
          }
          
          // Determine if paid in full based on program state
          let updatedProgram: any = null;
          let paidInFull = false;
          
          if (program) {
            paidInFull = checkIfPaidInFull(program);
            console.log('[PaymentHub] Calculated isPaidInFull from current program:', {
              programId: program.patientPaymentProgramId,
              pfrPendingBalance: program.patientPaymentSchedule?.pfrPendingBalance,
              isPaidInFull: paidInFull,
            });
          }
          
          // Update program schedule during enrollment flow (for recurring payments or full pay)
          if (isProgramSetup && programId && patientEncounterId && (recurringMethodId || isFullPayProgram)) {
            const scheduleResult = await updateProgramSchedule({
              programId,
              patientEncounterId: parseInt(patientEncounterId, 10),
              ...(recurringMethodId && { recurringUserPaymentMethodId: recurringMethodId }),
              ...(values.recurringPaymentDate && { nextPaymentDueDt: values.recurringPaymentDate }),
              // For full pay programs, set isPaidInFull to true; otherwise use calculated value
              isPaidInFull: isFullPayProgram ? true : paidInFull,
              // Always set ACH flags to true when activating program
              isAch: false,
              isAchVerified: true,
              authToken,
            });
            
            if (scheduleResult.program) {
              // Use the returned program from PATCH response
              updatedProgram = scheduleResult.program;
            }
          }

          // Track if enrollment toast was shown to avoid duplicate toasts
          let enrollmentToastShown = false;

          // Execute enrollment flow after successful payment (only for program setup/enrollment)
          if (isProgramSetup) {
            try {
              console.log('[PaymentHub] Starting enrollment flow (isProgramSetup=true)...');
              
              // Use the program from PATCH response, or fall back to the existing program
              const programForEnrollment = updatedProgram || program;
              
              if (!programForEnrollment) {
                console.error('[PaymentHub] No program data available for enrollment');
              } else if (!patientId || !patientEncounterId) {
                console.error('[PaymentHub] Missing required data for enrollment:', {
                  hasPatientId: !!patientId,
                  hasPatientEncounterId: !!patientEncounterId,
                });
              } else {
                // Re-calculate isPaidInFull with enrollment program data
                const enrollmentPaidInFull = checkIfPaidInFull(programForEnrollment);
                console.log('[PaymentHub] Enrollment isPaidInFull calculation:', {
                  programId: programForEnrollment.patientPaymentProgramId,
                  pfrPendingBalance: programForEnrollment.patientPaymentSchedule?.pfrPendingBalance,
                  isPaidInFull: enrollmentPaidInFull,
                });
                
                // Get Specialist client for CRM integration (used by enrollService)
                const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);

                // Resolve guarantor user — payorUserId was already resolved earlier in onSubmit
                // Always prefer billingDetails.email since the user may have edited the portal login email in the down payment section
                const selectedPayorForEnroll = payorsWithStatus.find(p => p.id?.toString() === values.payorName);
                const guarantor = await resolveGuarantorForEnrollment({
                  email: billingDetails.email || selectedPayorForEnroll?.email,
                  existingUserId: payorUserId,
                  firstName: billingDetails.firstName || selectedPayorForEnroll?.name?.split(' ')[0] || '',
                  lastName: billingDetails.lastName || selectedPayorForEnroll?.name?.split(' ').slice(1).join(' ') || '',
                  clientId: selectedEncounter!.clientId,
                  patientEncounterId: parseInt(patientEncounterId, 10),
                });

                console.log('[PaymentHub] Enrollment params:', {
                  patientId: parseInt(patientId!, 10),
                  patientEncounterId: parseInt(patientEncounterId, 10),
                  programId,
                  isPaidInFull: enrollmentPaidInFull,
                  hasClient: !!client,
                  clientId: client?.clientId,
                  guarantorEmail: guarantor.email,
                  guarantorUserId: guarantor.userId,
                });

                // Execute enrollment (contact info already updated above)
                const enrollResult = await executeEnroll({
                  patientId: parseInt(patientId!, 10),
                  patientEncounterId: parseInt(patientEncounterId, 10),
                  programId,
                  isPaidInFull: enrollmentPaidInFull,
                  client,
                  guarantorEmail: guarantor.email,
                  guarantorUserId: guarantor.userId,
                });

                console.log('[PaymentHub] Enrollment result:', {
                  success: enrollResult.success,
                  message: enrollResult.message,
                  documentSent: enrollResult.documentSent,
                  workflowUpdated: enrollResult.workflowUpdated,
                  iocUpdated: enrollResult.iocUpdated,
                  linkedUserUpdated: enrollResult.linkedUserUpdated,
                  integrationTriggered: enrollResult.integrationTriggered,
                });

                // Build combined enrollment + payment toast message
                if (enrollResult.success) {
                  const paymentMethodLabel = paymentSource === 'CARD' ? 'CC' : 'ACH';
                  const isPending = (paymentResult.status || '').toLowerCase() === 'pending';
                  const paymentMessage = isPending
                    ? `payment submitted for $${paymentAmount.toFixed(2)} with ${paymentMethodLabel} ending in ${newPaymentLast4}. Please check back later for status.`
                    : `payment successful for $${paymentAmount.toFixed(2)} with ${paymentMethodLabel} ending in ${newPaymentLast4}.`;
                  
                  dispatch(showStatus(`Enrollment complete and ${paymentMessage}`));
                  enrollmentToastShown = true;
                } else {
                  dispatch(showErrorStatus(`Enrollment failed: ${enrollResult.message}`));
                  enrollmentToastShown = true;
                }
              }
            } catch (enrollError: any) {
              console.error('[PaymentHub] Error during enrollment flow:', enrollError);
              dispatch(showErrorStatus(`Enrollment error: ${enrollError?.message || 'An unexpected error occurred'}`));
              enrollmentToastShown = true;
              // Don't fail the payment flow, just log the error
            }
          } else {
            console.log('[PaymentHub] Skipping enrollment flow (isProgramSetup=false) - regular payment');
          }

          // Build status-appropriate toast message with payment details (only if enrollment toast wasn't shown)
          const isPending = (paymentResult.status || '').toLowerCase() === 'pending';
          if (!enrollmentToastShown) {
            const paymentMethodLabel = paymentSource === 'CARD' ? 'CC' : 'ACH';
            const toastMessage = isPending
              ? `Payment submitted for $${paymentAmount.toFixed(2)} with ${paymentMethodLabel} ending in ${newPaymentLast4}. Please check back later for status.`
              : `Payment successful for $${paymentAmount.toFixed(2)} with ${paymentMethodLabel} ending in ${newPaymentLast4}.`;
            dispatch(showStatus(toastMessage));
          }

          // Send receipt after successful payment (not pending)
          // Backend will use provided email or fall back to guarantor/patient email
          const receiptPaymentId = paymentResult.payment?.paymentId;
          console.log('[PaymentHub] Receipt check - paymentId:', receiptPaymentId, 'isPending:', isPending, 'status:', paymentResult.status);
          if (receiptPaymentId && !isPending) {
            try {
              await patientService.PaymentReceipt(
                receiptPaymentId,
                values.receiptEmail || undefined
              );
              console.log('[PaymentHub] Receipt sent for payment:', receiptPaymentId);
            } catch (receiptError) {
              console.error('[PaymentHub] Failed to send receipt:', receiptError);
            }
          } else {
            console.log('[PaymentHub] Receipt not sent - paymentId:', receiptPaymentId, 'isPending:', isPending);
          }

          // Create payment note for all payments
          if (patientId && patientEncounterId && paymentAmount > 0) {
            await createPaymentNote(dispatch, {
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              amount: paymentAmount,
              paymentType: paymentSource === 'CARD' ? 'card' : 'ach',
              last4: newPaymentLast4,
              showNotify: false,
            });
          }

          // Send SPA document for non-enroll card/ACH payments only (enroll flow handles its own document generation)
          // const isElectronicPaymentForSpa = paymentSource === 'CARD' || paymentSource === 'ACH';
          // if (!isProgramSetup && patientEncounterId && isElectronicPaymentForSpa) {
          //   try {
          //     await patientService.spaDocument(parseInt(patientEncounterId, 10));
          //     console.log('[PaymentHub] SPA document sent for non-enroll payment');
          //   } catch (spaError) {
          //     console.error('[PaymentHub] Failed to send SPA document:', spaError);
          //   }
          // }

          // Trigger CRM update for all payments (not just enrollment)
          if (patientId && patientEncounterId) {
            const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);
            await triggerCrmUpdate({
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              client,
            });
          }

        // Create "pay at facility" note if checkbox is checked and amount is populated
        if (values.payAtFacility && values.payAtFacilityAmount) {
            const facilityAmount = parseAmount(values.payAtFacilityAmount);
            if (facilityAmount > 0 && patientId && patientEncounterId) {
              console.log('[PaymentHub] Creating pay at facility note:', { facilityAmount, patientId, patientEncounterId });
              await createPaymentNote(dispatch, {
                patientId: parseInt(patientId, 10),
                patientEncounterId: parseInt(patientEncounterId, 10),
                amount: facilityAmount,
                paymentType: 'facility',
                showNotify: false,
              });
            }
          }

          // Refetch payment methods to update the saved methods list
          await refetchPaymentMethods();
          setIsProcessing(false);
          
          navigate(getSuccessRoute(paymentChannelId, patientId));
          return;
        }

        // ============================================================================
        // CASH/CHECK PAYMENT FLOW
        // ============================================================================
        // This code only runs for CASH/CHECK payments since CARD/ACH return early above
        // Facility-only payments have already been handled and returned early
        // ============================================================================
        
        // Re-calculate amounts for Cash/Check flow
        const cashCheckPaymentAmount = parseAmount(values.amount);
        const cashCheckFacilityAmount = values.payAtFacility && values.payAtFacilityAmount 
          ? parseAmount(values.payAtFacilityAmount) 
          : 0;
        const isCashCheckFacilityOnly = cashCheckPaymentAmount === 0 && cashCheckFacilityAmount > 0;
        
        // Validate: must have either a payment amount or a facility amount
        if (cashCheckPaymentAmount <= 0 && cashCheckFacilityAmount <= 0) {
          dispatch(showErrorStatus('Please enter a payment amount or a facility amount'));
          setIsProcessing(false);
          return;
        }

        // Only create payment record if there's a payment amount > 0
        if (cashCheckPaymentAmount > 0) {
          if (!programId) {
            dispatch(showErrorStatus('Program ID is required to process payment'));
            setIsProcessing(false);
            return;
          }

          // Cash/Check always use ATFACILITY (2), regardless of conversion status
          const paymentTypeId = PAYMENT_TYPE.ATFACILITY;

          const paymentResult = await createPayment({
            programId,
            paymentRequest: {
              paymentAmt: cashCheckPaymentAmount,
              userPaymentMethodId: userPaymentMethodId || 0,
              paymentChannelId,
              paymentTypeId,
              ...(values.paymentReason && { paymentReason: values.paymentReason }),
            },
          });

          if (!paymentResult.success) {
            dispatch(showErrorStatus(paymentResult.message || 'Failed to process cash/check payment'));
            setIsProcessing(false);
            return;
          }

          console.log('[PaymentHub] Cash/Check payment created:', paymentResult.paymentIntentId, 'Status:', paymentResult.status);

          // Create payment note for cash/check payment
          if (patientId && patientEncounterId) {
            await createPaymentNote(dispatch, {
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              amount: cashCheckPaymentAmount,
              paymentType: paymentSource === 'CASH' ? 'cash' : 'check',
              showNotify: false,
            });
          }

          // Trigger CRM update for cash/check payments
          if (patientId && patientEncounterId) {
            const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);
            await triggerCrmUpdate({
              patientId: parseInt(patientId, 10),
              patientEncounterId: parseInt(patientEncounterId, 10),
              client,
            });
          }

          // Show success toast for cash/check (will be overridden if enrollment succeeds)
          if (!isProgramSetup) {
            dispatch(showStatus(paymentResult.message || `Payment successful! $${cashCheckPaymentAmount.toFixed(2)} paid with ${paymentSource.toLowerCase()}.`));
          }
        }

        // Execute enrollment flow for cash/check payments when in enrollment mode
        if (isProgramSetup && patientId && patientEncounterId && programId) {
          try {
            // Resolve guarantor user first — needed for saving new recurring payment methods
            const selectedCashPayor = payorsWithStatus.find(p => p.id?.toString() === values.payorName);
            const guarantor = await resolveGuarantorForEnrollment({
              email: billingDetails.email || selectedCashPayor?.email,
              existingUserId: values.payorName === NEW_PAYOR_ID 
                ? newChampionUserId 
                : (selectedCashPayor?.userId || (userLookupState.userId ? parseInt(userLookupState.userId, 10) : undefined)),
              firstName: billingDetails.firstName || selectedCashPayor?.name?.split(' ')[0] || '',
              lastName: billingDetails.lastName || selectedCashPayor?.name?.split(' ').slice(1).join(' ') || '',
              clientId: selectedEncounter.clientId,
              patientEncounterId: parseInt(patientEncounterId, 10),
            });

            // Resolve recurring payment method (existing selection or create new)
            let recurringMethodId: number | null = null;
            if (recurringPaymentMethodSectionRef.current) {
              const methodResult = await resolveRecurringPaymentMethod({
                sectionRef: recurringPaymentMethodSectionRef.current,
                userId: guarantor.userId,
                authToken,
                stripeCustomerId: recurringUserLookupState.stripeCustomerId || undefined,
              });
              if (!methodResult.success) {
                dispatch(showErrorStatus(methodResult.error || 'Failed to save recurring payment method.'));
                setIsProcessing(false);
                return;
              }
              recurringMethodId = methodResult.recurringMethodId;
            }
            
            await updateProgramSchedule({
              programId,
              patientEncounterId: parseInt(patientEncounterId, 10),
              ...(recurringMethodId && { recurringUserPaymentMethodId: recurringMethodId }),
              ...(values.recurringPaymentDate && { nextPaymentDueDt: values.recurringPaymentDate }),
              isPaidInFull: isFullPayProgram ? true : false,
              isAch: false,
              isAchVerified: true,
              authToken,
            });
            
            // Get the program for enrollment
            const programForEnrollment = fetchedProgram || program;
            
            if (programForEnrollment) {
              // Calculate isPaidInFull
              const enrollmentPaidInFull = isFullPayProgram ? true : checkIfPaidInFull(programForEnrollment);
              
              // Get client for CRM integration
              const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);

              // Execute enrollment (contact info already updated above)
              const enrollResult = await executeEnroll({
                patientId: parseInt(patientId, 10),
                patientEncounterId: parseInt(patientEncounterId, 10),
                programId,
                isPaidInFull: enrollmentPaidInFull,
                client,
                guarantorEmail: guarantor.email,
                guarantorUserId: guarantor.userId,
              });
              
              // Show enrollment toast
              if (enrollResult.success) {
                const paymentMessage = cashCheckPaymentAmount > 0
                  ? `payment successful for $${cashCheckPaymentAmount.toFixed(2)} with ${paymentSource.toLowerCase()}.`
                  : '';
                dispatch(showStatus(`Enrollment complete${paymentMessage ? ' and ' + paymentMessage : '.'}`));
              } else {
                dispatch(showErrorStatus(`Enrollment failed: ${enrollResult.message}`));
              }
            }
          } catch (enrollError: any) {
            dispatch(showErrorStatus(`Enrollment error: ${enrollError?.message || 'An unexpected error occurred'}`));
          }
        }

        // Trigger CRM update for facility-only payments (when no direct payment is made)
        if (isCashCheckFacilityOnly && patientId && patientEncounterId) {
          const client = allClients?.find(c => c.clientId === selectedEncounter?.clientId);
          await triggerCrmUpdate({
            patientId: parseInt(patientId, 10),
            patientEncounterId: parseInt(patientEncounterId, 10),
            client,
          });
        }

        // Create "pay at facility" note if checkbox is checked and amount is populated
        if (cashCheckFacilityAmount > 0 && patientId && patientEncounterId) {
          console.log('[PaymentHub] Creating pay at facility note (Cash/Check flow):', { cashCheckFacilityAmount, patientId, patientEncounterId });
          await createPaymentNote(dispatch, {
            patientId: parseInt(patientId, 10),
            patientEncounterId: parseInt(patientEncounterId, 10),
            amount: cashCheckFacilityAmount,
            paymentType: 'facility',
            showNotify: false,
          });
          
          // Update IOC workflow status for facility payment (only for pre-convert IOCs)
          const isConverted = selectedEncounter?.workflow?.workflowId === 3;
          
          if (!isConverted) {
            const pfrAmt = selectedEncounter?.adjustedPfrAmt ?? selectedEncounter?.pfrAmt ?? 0;
            const remainingAmt = pfrAmt - cashCheckPaymentAmount; // cashCheckPaymentAmount is the direct payment taken (0 for facility-only)
            
            let workflow;
            if (cashCheckFacilityAmount && remainingAmt === cashCheckFacilityAmount) {
              // Facility amount covers entire remaining PFR -> "Follow Up Needed - Pending Payment at Facility"
              workflow = workFlowStatus.preconvert.find(wf =>
                wf.workflowStatus.workflowStatusName === "Follow Up Needed" &&
                wf.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
              );
              console.log('[PaymentHub] Matched: Follow Up Needed - Pending Payment at Facility');
            } else if (cashCheckFacilityAmount && remainingAmt - cashCheckFacilityAmount > 0) {
              // Facility amount covers part of remaining PFR -> "New - Pending Payment at Facility"
              workflow = workFlowStatus.preconvert.find(wf =>
                wf.workflowStatus.workflowStatusName === "New" &&
                wf.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
              );
              console.log('[PaymentHub] Matched: New - Pending Payment at Facility');
            }
            
            console.log('[PaymentHub] Workflow to update:', workflow);
            
            if (workflow) {
              console.log('[PaymentHub] Updating IOC workflow for facility payment:', workflow);
              await patientService.saveStatus({
                workFlow: workflow as any,
                encounterId: parseInt(patientEncounterId, 10),
                patientId: parseInt(patientId, 10),
              });
              // Update the selected encounter in Redux with the new workflow
              dispatch(setSelectedEncounter({
                ...selectedEncounter,
                workflow: workflow as any,
              }));
            } else {
              console.log('[PaymentHub] No workflow matched for facility payment');
            }
          }
          
          // Show toast for facility-only payment
          if (isCashCheckFacilityOnly) {
            dispatch(showStatus(`Recorded $${cashCheckFacilityAmount.toFixed(2)} to be taken at facility.`));
          }
        }

        await refetchPaymentMethods();
        setIsProcessing(false);
        
        navigate(getSuccessRoute(paymentChannelId, patientId));
        return;
        
      } catch (error: any) {
        console.error('Payment error:', error);
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred processing the payment';
        dispatch(showErrorStatus(errorMessage));
      } finally {
        setIsProcessing(false);
      }
    },
  });

  const parsePayorName = (fullName: string): { firstName: string; lastName: string } => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  useEffect(() => {
    if (payorsWithStatus.length > 0) {
      const currentPayorName = formik.values.payorName;
      console.log('Payor selection effect - payorsWithStatus length:', payorsWithStatus.length);
      console.log('Current formik payorName:', currentPayorName);

      // Determine default payor: prefer guarantor, fall back to first payor (patient)
      const guarantorPayor = payorsWithStatus.find(p => p.isGuarantor);
      const defaultPayor = guarantorPayor || payorsWithStatus[0];
      const defaultPayorIdStr = defaultPayor.id?.toString() || '';

      // Helper to initialize payor state (used for both down payment and recurring)
      const initializePayorState = (
        payor: typeof defaultPayor,
        setDetails: typeof setBillingDetails,
        setLookupState: typeof setUserLookupState,
        setSelectedMethod?: typeof setSelectedSavedMethod
      ) => {
        const { firstName, lastName } = parsePayorName(payor.name);
        setDetails(prev => ({
          ...prev,
          firstName,
          lastName,
          email: payor.email || '',
        }));

        const payorPaymentMethods = payor.userLookup?.savedPaymentMethods || (payor as any).paymentMethods || [];
        const payorUserId = payor.userLookup?.user?.userId || (payor as any).userId || null;

        if (payorPaymentMethods.length > 0 || payorUserId) {
          const { defaultCardMethodId, stripeCustomerId } = getDefaultCardMethodAndCustomerId(payorPaymentMethods);
          setLookupState({
            isChecking: false,
            isChecked: true,
            userExists: !!payorUserId,
            userId: payorUserId?.toString() || null,
            stripeCustomerId,
            savedPaymentMethods: payorPaymentMethods as UserPaymentMethod[],
          });
          if (setSelectedMethod) {
            setSelectedMethod(defaultCardMethodId);
          }
        }

        return { payorPaymentMethods, payorUserId };
      };

      if (!currentPayorName || currentPayorName === '') {
        formik.setFieldValue('payorName', defaultPayorIdStr, true);
        formik.setFieldValue('receiptEmail', defaultPayor.email || '', true);

        const { payorPaymentMethods, payorUserId } = initializePayorState(
          defaultPayor,
          setBillingDetails,
          setUserLookupState,
          setSelectedSavedMethod
        );

        const guarantorDetails = getGuarantorAddressDetails(defaultPayor, selectedEncounter, selectedPatient);
        if (guarantorDetails) {
          setAddressDetails(guarantorDetails);
        }

        if (!(payorPaymentMethods.length > 0 || payorUserId) && defaultPayor.email) {
          lookupUserByEmail(defaultPayor.email);
        }
      } else {
        console.log('Payor already selected:', currentPayorName);
      }
      
      if (!recurringPayorId) {
        setRecurringPayorId(defaultPayorIdStr);
        initializePayorState(
          defaultPayor,
          setRecurringBillingDetails,
          setRecurringUserLookupState
        );

        const recurringGuarantorDetails = getGuarantorAddressDetails(defaultPayor, selectedEncounter, selectedPatient);
        if (recurringGuarantorDetails) {
          setRecurringAddressDetails(recurringGuarantorDetails);
        }
      }
    }
  }, [payorsWithStatus, formik.values.payorName, lookupUserByEmail, recurringPayorId]);


  const handlePayorChange = async (value: string) => {
    formik.setFieldValue('payorName', value, false);
    setSelectedSavedMethod(null);
    setIsDownPaymentGuarantorCheckboxChecked(false); // Reset checkbox when payor changes

    if (value === NEW_PAYOR_ID) {
      setIsAddingNewPayor(true);
      setNewPayorFirstName('');
      setNewPayorLastName('');
      setNewPayorEmail('');
      formik.setFieldValue('receiptEmail', '', false);
      setBillingDetails(prev => ({ ...prev, firstName: '', lastName: '', email: '' }));
      setUserLookupState({
        isChecking: false,
        isChecked: false,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
      // Note: PaymentIntent is created at payment time, not on payor selection
      return;
    }

    setIsAddingNewPayor(false);

    const selectedPayor = payorsWithStatus.find((p) => p.id?.toString() === value);
    if (selectedPayor) {
      formik.setFieldValue('receiptEmail', selectedPayor.email || '', false);

      const { firstName, lastName } = parsePayorName(selectedPayor.name);
      setBillingDetails(prev => ({
        ...prev,
        firstName,
        lastName,
        email: selectedPayor.email || '',
      }));

      // Pre-fill guarantor info if this is an existing guarantor
      const guarantorDetails = getGuarantorAddressDetails(selectedPayor, selectedEncounter, selectedPatient);
      if (guarantorDetails) {
        setAddressDetails(guarantorDetails);
      }

      // Check for payment methods - either from userLookup or directly on the payor
      const payorPaymentMethods = selectedPayor.userLookup?.savedPaymentMethods || (selectedPayor as any).paymentMethods || [];
      const payorUserId = selectedPayor.userLookup?.user?.userId || (selectedPayor as any).userId || null;
      
      if (payorPaymentMethods.length > 0 || payorUserId) {
        // Get default card method and Stripe customer ID
        const { defaultCardMethodId, stripeCustomerId } = getDefaultCardMethodAndCustomerId(payorPaymentMethods);
        
        setUserLookupState({
          isChecking: false,
          isChecked: true,
          userExists: !!payorUserId,
          userId: payorUserId?.toString() || null,
          stripeCustomerId,
          savedPaymentMethods: payorPaymentMethods as UserPaymentMethod[],
        });

        setSelectedSavedMethod(defaultCardMethodId);
        console.log('[PaymentHub] Set payment methods from payor:', payorPaymentMethods.length, 'total, default card:', defaultCardMethodId);
      } else {
        if (selectedPayor.email) {
          lookupUserByEmail(selectedPayor.email);
        }
      }
    }
  };

  const handleUseExistingSourceChange = (value: boolean) => {
    formik.setFieldValue('useExistingSource', value, false);
    if (!value) {
      formik.setFieldValue('paymentSources', '', false);
    }
  };

  // Handler for recurring payor change - similar to handlePayorChange but for recurring payment
  const handleRecurringPayorChange = async (value: string) => {
    setRecurringPayorId(value);
    setIsRecurringGuarantorCheckboxChecked(false); // Reset checkbox when payor changes
    
    // Reset the recurring payment method section when payor changes
    recurringPaymentMethodSectionRef.current?.reset();

    if (value === NEW_PAYOR_ID) {
      setIsAddingNewRecurringPayor(true);
      setNewRecurringPayorFirstName('');
      setNewRecurringPayorLastName('');
      setRecurringBillingDetails({ firstName: '', lastName: '', email: '' });
      setRecurringUserLookupState({
        isChecking: false,
        isChecked: false,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
      return;
    }

    setIsAddingNewRecurringPayor(false);

    const selectedPayor = payorsWithStatus.find((p) => p.id?.toString() === value);
    if (selectedPayor) {
      const { firstName, lastName } = parsePayorName(selectedPayor.name);
      setRecurringBillingDetails({
        firstName,
        lastName,
        email: selectedPayor.email || '',
      });
      // If same payor as down payment and guarantor form was filled, copy that data
      // Otherwise, get data from the payor's existing info
      const isSameAsDownPaymentPayor = value === formik.values.payorName;
      const downPaymentFormHasData = addressDetails.phone || addressDetails.birthDate || addressDetails.streetAddress1;
      
      if (isSameAsDownPaymentPayor && downPaymentFormHasData) {
        // Copy down payment guarantor form data to recurring
        setRecurringAddressDetails({ ...addressDetails });
      } else {
        const guarantorDetails = getGuarantorAddressDetails(selectedPayor, selectedEncounter, selectedPatient);
        if (guarantorDetails) {
          setRecurringAddressDetails(guarantorDetails);
        }
      }

      // Check for payment methods - either from userLookup or directly on the payor
      const payorPaymentMethods = selectedPayor.userLookup?.savedPaymentMethods || (selectedPayor as any).paymentMethods || [];
      const payorUserId = selectedPayor.userLookup?.user?.userId || (selectedPayor as any).userId || null;
      
      if (payorPaymentMethods.length > 0 || payorUserId) {
        const firstMethod = payorPaymentMethods[0];
        const stripeCustomerId = (firstMethod as any)?.extPaymentUserId || null;
        
        setRecurringUserLookupState({
          isChecking: false,
          isChecked: true,
          userExists: !!payorUserId,
          userId: payorUserId?.toString() || null,
          stripeCustomerId,
          savedPaymentMethods: payorPaymentMethods as UserPaymentMethod[],
        });
        console.log('[PaymentHub] Set recurring payment methods from payor:', payorPaymentMethods.length, 'methods');
      } else if (selectedPayor.email) {
        // Look up user by email for recurring payor
        setRecurringUserLookupState(prev => ({ ...prev, isChecking: true }));
        try {
          const userResponse = await userService.getAllUsers(0, ApiBatchLimits.users, { userName: selectedPayor.email });
          const userRecord = userResponse?.entity?.[0];
          let savedPaymentMethods: UserPaymentMethod[] = [];
          let stripeCustomerId: string | null = null;

          if (userRecord?.userId) {
            const pmResult = await fetchUserPaymentMethods({ userId: userRecord.userId, authToken });
            if (pmResult.success) {
              savedPaymentMethods = pmResult.paymentMethods;
            }
          }

          const { stripeCustomerId: customerId } = getDefaultCardMethodAndCustomerId(savedPaymentMethods);
          stripeCustomerId = customerId;

          setRecurringUserLookupState({
            isChecking: false,
            isChecked: true,
            userExists: !!userRecord,
            userId: userRecord?.userId?.toString() || null,
            stripeCustomerId,
            savedPaymentMethods,
          });
        } catch (error) {
          console.error('[PaymentHub] Error looking up recurring payor user:', error);
          setRecurringUserLookupState({
            isChecking: false,
            isChecked: true,
            userExists: false,
            userId: null,
            stripeCustomerId: null,
            savedPaymentMethods: [],
          });
        }
      }
    }
  };

  const convertPaymentMethodToReadableInfo = (method: StripePaymentMethod): string => {
    const brand = method.brand?.charAt(0).toUpperCase() + method.brand?.slice(1) || 'Card';
    return `${brand} ending in ${method.last4}`;
  };

  // Handle payment source tab change - create new SetupIntent with appropriate payment method types
  const handlePaymentSourceChange = async (newSource: keyof typeof PAYMENT_SOURCE_TYPES) => {
    setPaymentSource(newSource);
    
    // Reset validation state for amount fields when payment method changes
    // This clears any validation errors since minimum amounts differ between payment methods
    formik.setFieldTouched('amount', false, false);
    formik.setFieldError('amount', undefined);
    
    // Cash/Check cannot be used for recurring payments - automatically set useForRecurring to false
    if (newSource === 'CASH' || newSource === 'CHECK') {
      formik.setFieldValue('useForRecurring', false, false);
    }

    // Create new SetupIntent with appropriate payment method types when switching between Card and ACH
    if (newSource === 'CARD' || newSource === 'ACH') {
      setSetupIntentClientSecret(null);
      setIsSetupIntentLoading(true);
      
      // paymentMethodTypeId: 1 = Card, 2 = BankAccount
      const paymentMethodTypeId = newSource === 'ACH' ? 2 : 1;
      
      console.log('[PaymentHub] Creating new SetupIntent for payment method type ID:', paymentMethodTypeId);
      
      const result = await createSetupIntent({ paymentMethodTypeId });
      
      if (result.success && result.clientSecret && result.setupIntentId) {
        setSetupIntentClientSecret(result.clientSecret);
        setSetupIntentId(result.setupIntentId);
        console.log('[PaymentHub] New SetupIntent created:', result.setupIntentId);
      } else {
        console.warn('[PaymentHub] Failed to create SetupIntent:', result.message);
      }
      
      setIsSetupIntentLoading(false);
    }
  };

  const hasSavedCardMethods = filterCardMethods(userLookupState.savedPaymentMethods).length > 0;
  const showNewPaymentMethodForm = 
    paymentSource === 'CARD' && 
    (!hasSavedCardMethods || selectedSavedMethod === 'new' || (!selectedSavedMethod && !formik.values.useExistingSource));

  const isLoading = isPatientLoading || isEncounterLoading || isPreCheckingPayors;

  if (isLoading) {
    return (
      <main>
        <div className="content px-4 py-4">
          <LoadingOverlay />
        </div>
      </main>
    );
  }

  return (
    <main test-id="payment-hub-view">
      <div className="content px-4 py-4">
          <Paper elevation={2} sx={{ p: 4, maxWidth: 950, mx: 'auto' }} test-id="payment-hub-form-paper">
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            {isProgramSetup ? 'Complete Program' : 'Take Payment'}
          </Typography>

          <Grid container spacing={2.5}>
            {/* Patient Name */}
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Patient Name
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right" test-id="payment-hub-patient-name">
                {patientName}
              </Typography>
            </Grid>

            {/* Pending Remaining Balance */}
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Pending Remaining Balance
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
              <Typography variant="body1" align="right" test-id="payment-hub-pending-balance">
                {formatNumberToUSD(pendingBalance)}
              </Typography>
            </Grid>

            {/* Payment Amount Section */}
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {isProgramSetup ? 'Down Payment Amount' : 'Payment Amount'}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
              {(isZeroDownPayment || isDownPaymentFullyPaid) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1" align="right" sx={{ fontWeight: 'bold', color: 'green' }}>
                    No Payment Required
                  </Typography>
                  <Tooltip 
                    title={isZeroDownPayment 
                      ? "This is a zero down payment program" 
                      : "The total down payment amount has already been paid"
                    }
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Box sx={{ width: 250, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                    <TextField
                      label="Amount"
                      startAdornment={<AttachMoneyIcon />}
                      name="amount"
                      value={formik.values.amount}
                      disabled={isProcessing || isAmountDisabled}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue('amount', stripCurrencyInput(e.target.value), true);
                        formik.setFieldTouched('amount', true, false);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        formik.handleBlur(e);
                        const value = parseAmount(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          formik.setFieldValue('amount', formatCurrencyInput(value), true);
                        }
                      }}
                      type="text"
                      test-id="payment-hub-amount-input"
                    />
                  </Box>
                  {(formik.touched.amount || formik.submitCount > 0) && formik.errors.amount && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#d32f2f', 
                        mt: '4px',
                        width: 250,
                        textAlign: 'left',
                        fontWeight: 400,
                        lineHeight: 1.66,
                        letterSpacing: '0.03333em',
                      }}
                    >
                      {formik.errors.amount}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>

            {/* Who is paying? Section */}
            {!isMobile && !isZeroDownPayment && !isDownPaymentFullyPaid && (
              <>
                <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Who is paying?
                  </Typography>
                </Grid>
                <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
                  <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
                    <Box sx={{ 
                      width: '100%', 
                      maxWidth: 400,
                      position: 'relative',
                      '& .MuiInputBase-root': { height: 56 },
                      ...(payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName)?.isGuarantor ? {
                        '& .MuiInputBase-input': {
                          color: 'green',
                          fontWeight: 'bold',
                        },
                      } : {}),
                    }}>
                      <Tooltip title="Green indicates Guarantor" arrow>
                        <InfoOutlinedIcon 
                          sx={{ 
                            fontSize: '14px', 
                            color: 'text.secondary', 
                            cursor: 'help',
                            position: 'absolute',
                            left: '50px',
                            top: '-8px',
                            zIndex: 1,
                            backgroundColor: 'white',
                            padding: '0 2px',
                          }} 
                        />
                      </Tooltip>
                      <TextField
                        select
                        error={formik.touched.payorName && formik.errors.payorName}
                        label="Name"
                        name="payorName"
                        value={formik.values.payorName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePayorChange(e.target.value)}
                        onBlur={formik.handleBlur}
                        disabled={isProcessing}
                        test-id="payment-hub-payor-select"
                      >
                        {payorsWithStatus.length === 0 ? (
                          <MenuItem disabled>Loading payors...</MenuItem>
                        ) : (
                          payorsWithStatus.map((payor) => {
                            const isGuarantor = payor.isGuarantor;
                            return (
                              <MenuItem 
                                key={payor.id} 
                                value={payor.id?.toString()}
                                sx={isGuarantor ? { 
                                  color: 'green', 
                                  fontWeight: 'bold',
                                  '&.Mui-selected': {
                                    color: 'green',
                                    fontWeight: 'bold',
                                    backgroundColor: 'rgba(0, 128, 0, 0.08)',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(0, 128, 0, 0.12)',
                                  },
                                } : {}}
                              >
                                {payor.name}
                              </MenuItem>
                            );
                          })
                        )}
                        <Divider />
                        <MenuItem value={NEW_PAYOR_ID} sx={{ color: 'primary.main' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AddIcon fontSize="small" color="primary" />
                            <Typography variant="body2" color="primary">
                              Add new champion
                            </Typography>
                          </Box>
                        </MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                </Grid>
              </>
            )}

            {/* Make selected payor guarantor checkbox - show for new champions or existing champions who aren't already guarantors */}
            {/* Make selected payor guarantor checkbox - also shown for full pay programs where there is no recurring section */}
            {((isProgramSetup && (formik.values.useForRecurring || isFullPayProgram) && !isZeroDownPayment && !isDownPaymentFullyPaid) || !isProgramSetup) && !isMobile && formik.values.payorName && (() => {
              
              // For new champion
              if (formik.values.payorName === NEW_PAYOR_ID) {
                const newChampionName = newPayorFirstName && newPayorLastName 
                  ? `${newPayorFirstName} ${newPayorLastName}`.trim()
                  : 'new champion';
                return (
                  <Grid item xs={12} sx={{ py: 0.5 }}>
                    <Tooltip title={isConverted ? "Cannot change guarantor after Enrollment" : ""} arrow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isDownPaymentGuarantorCheckboxChecked}
                            onChange={(e) => {
                              setIsDownPaymentGuarantorCheckboxChecked(e.target.checked);
                              if (e.target.checked) {
                                setIsRecurringGuarantorCheckboxChecked(false);
                                setAddressDetails({
                                  streetAddress1: '',
                                  city: '',
                                  state: '',
                                  zipCode: '',
                                  phone: '',
                                  birthDate: '',
                                  relationshipToPatient: '',
                                });
                              }
                            }}
                            disabled={isProcessing || isConverted}
                            test-id="make-new-champion-guarantor-checkbox"
                          />
                        }
                        label={`Make ${newChampionName} the guarantor`}
                      />
                    </Tooltip>
                  </Grid>
                );
              }
              
              // For existing champion
              const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName);
              const isAlreadyGuarantor = selectedPayor?.isGuarantor || false;
              if (!selectedPayor || isAlreadyGuarantor) return null;
              return (
                <Grid item xs={12} sx={{ py: 0.5 }}>
                  <Tooltip title={isConverted ? "Cannot change guarantor after enrollment" : ""} arrow>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isDownPaymentGuarantorCheckboxChecked}
                          onChange={(e) => {
                            setIsDownPaymentGuarantorCheckboxChecked(e.target.checked);
                            if (e.target.checked) {
                              setIsRecurringGuarantorCheckboxChecked(false);
                              if (selectedPayor.type === 'patient') {
                                setAddressDetails({
                                  streetAddress1: selectedPatient?.contact?.primaryAddress?.streetAddress1 || '',
                                  city: selectedPatient?.contact?.primaryAddress?.city || '',
                                  state: selectedPatient?.contact?.primaryAddress?.state?.stateCode || '',
                                  zipCode: selectedPatient?.contact?.primaryAddress?.zipCode || '',
                                  phone: Utils.formatPhoneNumber(selectedPatient?.contact?.primPhoneNum || ''),
                                  birthDate: selectedPatient?.contact?.birthDate || '',
                                  relationshipToPatient: 'Self',
                                });
                              } else {
                                const champion = selectedEncounter?.patientChampion?.find(
                                  c => c.patientChampionId === selectedPayor.id
                                );
                                if (champion) {
                                  setAddressDetails({
                                    streetAddress1: champion.contact?.primaryAddress?.streetAddress1 || '',
                                    city: champion.contact?.primaryAddress?.city || '',
                                    state: champion.contact?.primaryAddress?.state?.stateCode || '',
                                    zipCode: champion.contact?.primaryAddress?.zipCode || '',
                                    phone: Utils.formatPhoneNumber(champion.contact?.primPhoneNum || ''),
                                    birthDate: champion.contact?.birthDate || '',
                                    relationshipToPatient: champion.relationshipToPatient || '',
                                  });
                                }
                              }
                            }
                          }}
                          disabled={isProcessing || isConverted}
                          test-id="make-payor-guarantor-checkbox"
                        />
                      }
                      label={`Make ${selectedPayor.name} the guarantor`}
                    />
                  </Tooltip>
                </Grid>
              );
            })()}

            {/* Guarantor Info Fields - show when making someone a guarantor or when existing guarantor is selected */}
            {/* Guarantor Info Fields - also shown for full pay programs where there is no recurring section */}
            {((isProgramSetup && (formik.values.useForRecurring || isFullPayProgram) && !isZeroDownPayment && !isDownPaymentFullyPaid) || !isProgramSetup) && !isMobile && !isRecurringGuarantorCheckboxChecked && (isDownPaymentGuarantorCheckboxChecked || payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName)?.isGuarantor) && (
              <Grid item xs={12}>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Guarantor Information
                  </Typography>
                  <GuarantorAddressFields
                    addressDetails={addressDetails}
                    onAddressDetailsChange={(field, value) => setAddressDetails(prev => ({ ...prev, [field]: value }))}
                    testIdPrefix="guarantor"
                    showNameFields={isAddingNewPayor && isDownPaymentGuarantorCheckboxChecked}
                    nameDetails={billingDetails}
                    onNameDetailsChange={(field, value) => {
                      setBillingDetails(prev => ({ ...prev, [field]: value }));
                      if (field === 'firstName') setNewPayorFirstName(value);
                      if (field === 'lastName') setNewPayorLastName(value);
                    }}
                    isEnrollmentFlow={isProgramSetup}
                    isPatientPayor={payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName)?.type === 'patient'}
                  />
                </Box>
              </Grid>
            )}

            {/* Payment Method Section with Payment Source Toggle */}
            {!isMobile && !isZeroDownPayment && !isDownPaymentFullyPaid && (
              <>
                <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Payment Method
                  </Typography>
                </Grid>
                <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
                  <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
                    <Box sx={{ width: 'auto', minWidth: 250 }}>
                      <PaymentSourceTabs
                        value={paymentSource}
                        onChange={(newSource) => handlePaymentSourceChange(newSource as keyof typeof PAYMENT_SOURCE_TYPES)}
                        minWidth={250}
                      />
                    </Box>
                  </Box>
                </Grid>
              </>
            )}

          {/* Saved Payment Methods (for existing users) - Cards only */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && paymentSource === 'CARD' && userLookupState.userExists && filterCardMethods(userLookupState.savedPaymentMethods).length > 0 && (
            <Grid item xs={12}>
              <PaymentMethodList
                savedPaymentMethods={userLookupState.savedPaymentMethods}
                paymentSource="CARD"
                selectedMethodId={selectedSavedMethod}
                onMethodSelect={(methodId) => setSelectedSavedMethod(methodId)}
                showDefaultChip={true}
                onDeleteMethod={handleDeleteDownPaymentMethod}
                deletingMethodId={deletingMethodId}
              />
            </Grid>
          )}

          {/* Card Payment Form (Stripe Elements) - for new payment methods */}
          {/* Always render when Card is selected to create intent early, but hide when using saved method */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && paymentSource === 'CARD' && (
            <Grid item xs={12} sx={{ display: showNewPaymentMethodForm ? 'block' : 'none' }}>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                {!userLookupState.userExists && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your payment details below. Your information will be saved securely.
                  </Typography>
                )}
                <PaymentHubCardForm
                  key={`card-form-${setupIntentClientSecret}`}
                  ref={cardPaymentRef}
                  isActive={paymentSource === 'CARD'}
                  clientSecret={setupIntentClientSecret}
                  isLoading={isSetupIntentLoading}
                  billingDetails={billingDetails}
                  onBillingDetailsChange={(field, value) => {
                    setBillingDetails(prev => ({ ...prev, [field]: value }));
                    if (isAddingNewPayor) {
                      if (field === 'firstName') setNewPayorFirstName(value);
                      if (field === 'lastName') setNewPayorLastName(value);
                    }
                    if (field === 'email') {
                      formik.setFieldValue('receiptEmail', value, false);
                    }
                  }}
                  showNameFields={isAddingNewPayor && !isDownPaymentGuarantorCheckboxChecked}
                  showEmailField={isAddingNewPayor || !userLookupState.userId}
                />
              </Box>
            </Grid>
          )}

          {/* ACH Payment Form - for bank account payments */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && paymentSource === 'ACH' && (
            <Grid item xs={12}>
              <PaymentHubACHForm
                  ref={achPaymentRef}
                  isActive={paymentSource === 'ACH'}
                  billingDetails={billingDetails}
                  onBillingDetailsChange={(field, value) => {
                    setBillingDetails(prev => ({ ...prev, [field]: value }));
                    if (isAddingNewPayor) {
                      if (field === 'firstName') setNewPayorFirstName(value);
                      if (field === 'lastName') setNewPayorLastName(value);
                    }
                    if (field === 'email') {
                      formik.setFieldValue('receiptEmail', value, false);
                    }
                  }}
                  showNameFields={isAddingNewPayor && !isDownPaymentGuarantorCheckboxChecked}
                  showEmailField={isAddingNewPayor || !userLookupState.userId}
                  savedBankAccounts={filterBankAccounts(userLookupState.savedPaymentMethods)}
                  onSavedMethodSelect={setSelectedACHMethodId}
                  selectedSavedMethodId={selectedACHMethodId}
                  onDeleteSavedMethod={handleDeleteDownPaymentMethod}
                  deletingMethodId={deletingMethodId}
                />
            </Grid>
          )}

          {/* Use Existing Payment Method Toggle (legacy - from contact's Stripe methods) */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && paymentMethods.length > 0 && paymentSource === 'CARD' && !userLookupState.savedPaymentMethods.length && (
            <>
              <Grid item xs={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Use Existing Payment Method
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end">
                  <Toggle
                    name="useExistingSource"
                    value={formik.values.useExistingSource}
                    formik={formik}
                    disabled={isProcessing}
                    onChange={() => handleUseExistingSourceChange(!formik.values.useExistingSource)}
                  />
                </Box>
              </Grid>
            </>
          )}

          {/* Existing Payment Methods Dropdown (legacy) */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && formik.values.useExistingSource && (
            <Grid item xs={8}>
              <TextField
                select
                error={formik.touched.paymentSources && formik.errors.paymentSources}
                label="Existing Payment Methods"
                name="paymentSources"
                value={formik.values.paymentSources}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isProcessing}
                test-id="payment-hub-existing-methods-select"
              >
                <MenuItem value="-1">Select Payment Source</MenuItem>
                {paymentMethods.map((method, index) => (
                  <MenuItem
                    key={method.id || index}
                    value={JSON.stringify(method)}
                  >
                    {convertPaymentMethodToReadableInfo(method)}
                    {method.isDefaultSource ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Cash/Check billing details and message */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && (paymentSource === 'CASH' || paymentSource === 'CHECK') && (
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                {/* Billing details for Cash/Check - same style as Stripe form */}
                <Box sx={{ mb: 2 }}>
                  {/* First/Last name for new payors only - hide when guarantor form will show name fields */}
                  {isAddingNewPayor && !(isDownPaymentGuarantorCheckboxChecked && ((isProgramSetup && formik.values.useForRecurring) || !isProgramSetup)) && (
                    <Box sx={{ display: 'flex', gap: '12px', mb: '12px' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          component="label"
                          sx={{
                            display: 'block',
                            fontSize: '14.88px',
                            fontWeight: 400,
                            color: '#30313d',
                            mb: '4px',
                            lineHeight: '17.856px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                          }}
                        >
                          First name
                        </Box>
                        <Box
                          component="input"
                          type="text"
                          placeholder="First name"
                          value={billingDetails.firstName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setBillingDetails(prev => ({ ...prev, firstName: e.target.value }));
                            setNewPayorFirstName(e.target.value);
                          }}
                          onBlur={() => handleCashCheckBlur('firstName')}
                          test-id="payment-hub-cash-check-first-name"
                          sx={{
                            width: '100%',
                            height: '44.16px',
                            padding: '12px',
                            fontSize: '16px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                            color: '#30313d',
                            backgroundColor: '#fff',
                            border: `1px solid ${getCashCheckError('firstName') ? '#df1b41' : '#e6e6e6'}`,
                            borderRadius: '5px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': { borderColor: getCashCheckError('firstName') ? '#df1b41' : '#ccc' },
                            '&:focus': { borderColor: getCashCheckError('firstName') ? '#df1b41' : '#0570de', boxShadow: getCashCheckError('firstName') ? '0 0 0 3px rgba(223, 27, 65, 0.25)' : '0 0 0 3px rgba(5, 112, 222, 0.25)' },
                            '&::placeholder': { color: '#6d6e78' },
                          }}
                        />
                        {getCashCheckError('firstName') && (
                          <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                            {getCashCheckError('firstName')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          component="label"
                          sx={{
                            display: 'block',
                            fontSize: '14.88px',
                            fontWeight: 400,
                            color: '#30313d',
                            mb: '4px',
                            lineHeight: '17.856px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                          }}
                        >
                          Last name
                        </Box>
                        <Box
                          component="input"
                          type="text"
                          placeholder="Last name"
                          value={billingDetails.lastName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setBillingDetails(prev => ({ ...prev, lastName: e.target.value }));
                            setNewPayorLastName(e.target.value);
                          }}
                          onBlur={() => handleCashCheckBlur('lastName')}
                          test-id="payment-hub-cash-check-last-name"
                          sx={{
                            width: '100%',
                            height: '44.16px',
                            padding: '12px',
                            fontSize: '16px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                            color: '#30313d',
                            backgroundColor: '#fff',
                            border: `1px solid ${getCashCheckError('lastName') ? '#df1b41' : '#e6e6e6'}`,
                            borderRadius: '5px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': { borderColor: getCashCheckError('lastName') ? '#df1b41' : '#ccc' },
                            '&:focus': { borderColor: getCashCheckError('lastName') ? '#df1b41' : '#0570de', boxShadow: getCashCheckError('lastName') ? '0 0 0 3px rgba(223, 27, 65, 0.25)' : '0 0 0 3px rgba(5, 112, 222, 0.25)' },
                            '&::placeholder': { color: '#6d6e78' },
                          }}
                        />
                        {getCashCheckError('lastName') && (
                          <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                            {getCashCheckError('lastName')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  
                  {(isAddingNewPayor || !userLookupState.userId) && (
                    <PortalLoginEmailField
                      email={billingDetails.email}
                      onChange={(value) => {
                        setBillingDetails(prev => ({ ...prev, email: value }));
                        formik.setFieldValue('receiptEmail', value, false);
                      }}
                      onBlur={() => handleCashCheckBlur('email')}
                      error={getCashCheckError('email')}
                      testId="payment-hub-cash-check-email"
                    />
                  )}
                </Box>

                {/* Payment method message */}
                <Typography variant="body2" color="text.secondary">
                  {paymentSource === 'CASH'
                    ? 'Cash payment will be recorded manually.'
                    : 'Check payment will be recorded manually.'}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Divider - hide for zero down payment programs and fully paid down payment since there's no down payment section above */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
          )}

          {/* Use for Recurring Payment Toggle - hide for full pay programs, zero down payment programs, and fully paid down payment */}
          {!isZeroDownPayment && !isDownPaymentFullyPaid && isProgramSetup && !isPaidInFull && !program?.isPaidInFull && (
            <>
              <Grid item xs={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Use for Recurring Payment
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end" flexDirection="column" alignItems="flex-end">
                  <Toggle
                    name="useForRecurring"
                    value={formik.values.useForRecurring}
                    formik={formik}
                    disabled={isProcessing || paymentSource === 'CASH' || paymentSource === 'CHECK'}
                    onChange={() => {
                      const newValue = !formik.values.useForRecurring;
                      formik.setFieldValue('useForRecurring', newValue, false);
                      if (newValue) {
                        recurringPaymentMethodSectionRef.current?.reset();
                      } else {
                        const downPaymentPayorId = formik.values.payorName;
                        const downPaymentFormHasData = addressDetails.phone || addressDetails.birthDate || 
                          addressDetails.streetAddress1 || addressDetails.relationshipToPatient;
                        
                        if (downPaymentPayorId && downPaymentPayorId !== NEW_PAYOR_ID) {
                          setRecurringPayorId(downPaymentPayorId);
                          setIsAddingNewRecurringPayor(false);
                          setRecurringBillingDetails({ ...billingDetails });
                          if (isDownPaymentGuarantorCheckboxChecked) {
                            setIsRecurringGuarantorCheckboxChecked(true);
                            setIsDownPaymentGuarantorCheckboxChecked(false);
                          }
                          
                          if (downPaymentFormHasData) {
                            const dataToCopy = { ...addressDetails };
                            setTimeout(() => setRecurringAddressDetails(dataToCopy), 0);
                          }
                          
                          setRecurringUserLookupState({ ...userLookupState });
                        }
                      }
                    }}
                  />
                  {/* Recurring Payment Date Picker - always show when toggle section is visible */}
                  <Box mt={2} test-id="payment-hub-recurring-date-picker">
                    <DatePicker
                      error={
                        formik.touched.recurringPaymentDate &&
                        formik.errors.recurringPaymentDate
                      }
                      label="Recurring Payment Start Date"
                      minDate={getDateInFuture(1)}
                      maxDate={getDateInFuture(60)}
                      daysToDisable={[29, 30, 31]}
                      disabled={isProcessing}
                      value={formik.values.recurringPaymentDate}
                      onChange={(date: Date | null) => {
                        formik.setFieldValue('recurringPaymentDate', date ? date.toISOString() : '', false);
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              
              {/* Show payor selection and payment method selection when toggle is OFF */}
              {!formik.values.useForRecurring && (
                <>
                  {/* Recurring Payor Selection */}
                  <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Who is paying for recurring?
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
                    <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
                      <Box sx={{ 
                        width: '100%', 
                        maxWidth: 400,
                        position: 'relative',
                        '& .MuiInputBase-root': { height: 56 },
                        ...(payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.isGuarantor ? {
                          '& .MuiInputBase-input': {
                            color: 'green',
                            fontWeight: 'bold',
                          },
                        } : {}),
                      }}>
                        <Tooltip title="Green indicates Guarantor" arrow>
                          <InfoOutlinedIcon 
                            sx={{ 
                              fontSize: '14px', 
                              color: 'text.secondary', 
                              cursor: 'help',
                              position: 'absolute',
                              left: '50px',
                              top: '-8px',
                              zIndex: 1,
                              backgroundColor: 'white',
                              padding: '0 2px',
                            }} 
                          />
                        </Tooltip>
                        <TextField
                          select
                          label="Name"
                          name="recurringPayorName"
                          value={recurringPayorId}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRecurringPayorChange(e.target.value)}
                          onBlur={() => {}}
                          disabled={isProcessing}
                          test-id="recurring-payor-dropdown"
                        >
                          {payorsWithStatus.length === 0 ? (
                            <MenuItem disabled>Loading payors...</MenuItem>
                          ) : (
                            payorsWithStatus.map((payor) => {
                              const isGuarantor = payor.isGuarantor;
                              return (
                                <MenuItem 
                                  key={payor.id} 
                                  value={payor.id?.toString()}
                                  sx={isGuarantor ? { 
                                    color: 'green', 
                                    fontWeight: 'bold',
                                    '&.Mui-selected': {
                                      color: 'green',
                                      fontWeight: 'bold',
                                      backgroundColor: 'rgba(0, 128, 0, 0.08)',
                                    },
                                    '&.Mui-selected:hover': {
                                      backgroundColor: 'rgba(0, 128, 0, 0.12)',
                                    },
                                  } : {}}
                                >
                                  {payor.name}
                                </MenuItem>
                              );
                            })
                          )}
                          <Divider />
                          <MenuItem value={NEW_PAYOR_ID} sx={{ color: 'primary.main' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <AddIcon fontSize="small" color="primary" />
                              <Typography variant="body2" color="primary">
                                Add new champion
                              </Typography>
                            </Box>
                          </MenuItem>
                        </TextField>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Make recurring payor guarantor checkbox (enrollment flows only) */}
                  {isProgramSetup && recurringPayorId && (() => {
                    if (recurringPayorId === NEW_PAYOR_ID) {
                      const newChampionName = newRecurringPayorFirstName && newRecurringPayorLastName 
                        ? `${newRecurringPayorFirstName} ${newRecurringPayorLastName}`.trim()
                        : 'new champion';
                      return (
                        <Grid item xs={12} sx={{ py: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isRecurringGuarantorCheckboxChecked}
                                onChange={(e) => {
                                  setIsRecurringGuarantorCheckboxChecked(e.target.checked);
                                  if (e.target.checked) {
                                    setIsDownPaymentGuarantorCheckboxChecked(false);
                                    setRecurringAddressDetails({
                                      streetAddress1: '',
                                      city: '',
                                      state: '',
                                      zipCode: '',
                                      phone: '',
                                      birthDate: '',
                                      relationshipToPatient: '',
                                    });
                                  }
                                }}
                                disabled={isProcessing}
                                test-id="make-recurring-champion-guarantor-checkbox"
                              />
                            }
                            label={`Make ${newChampionName} the guarantor`}
                          />
                        </Grid>
                      );
                    }
                    
                    const selectedRecurringPayor = payorsWithStatus.find(p => p.id?.toString() === recurringPayorId);
                    const isAlreadyGuarantor = selectedRecurringPayor?.isGuarantor || false;
                    if (!selectedRecurringPayor || isAlreadyGuarantor) return null;
                    return (
                      <Grid item xs={12} sx={{ py: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isRecurringGuarantorCheckboxChecked}
                              onChange={(e) => {
                                setIsRecurringGuarantorCheckboxChecked(e.target.checked);
                                if (e.target.checked) {
                                  setIsDownPaymentGuarantorCheckboxChecked(false);
                                  if (selectedRecurringPayor.type === 'patient') {
                                    setRecurringAddressDetails({
                                      streetAddress1: selectedPatient?.contact?.primaryAddress?.streetAddress1 || '',
                                      city: selectedPatient?.contact?.primaryAddress?.city || '',
                                      state: selectedPatient?.contact?.primaryAddress?.state?.stateCode || '',
                                      zipCode: selectedPatient?.contact?.primaryAddress?.zipCode || '',
                                      phone: Utils.formatPhoneNumber(selectedPatient?.contact?.primPhoneNum || ''),
                                      birthDate: selectedPatient?.contact?.birthDate || '',
                                      relationshipToPatient: 'Self',
                                    });
                                  } else {
                                    const champion = selectedEncounter?.patientChampion?.find(
                                      c => c.patientChampionId === selectedRecurringPayor.id
                                    );
                                    if (champion) {
                                      setRecurringAddressDetails({
                                        streetAddress1: champion.contact?.primaryAddress?.streetAddress1 || '',
                                        city: champion.contact?.primaryAddress?.city || '',
                                        state: champion.contact?.primaryAddress?.state?.stateCode || '',
                                        zipCode: champion.contact?.primaryAddress?.zipCode || '',
                                        phone: Utils.formatPhoneNumber(champion.contact?.primPhoneNum || ''),
                                        birthDate: champion.contact?.birthDate || '',
                                        relationshipToPatient: champion.relationshipToPatient || '',
                                      });
                                    }
                                  }
                                }
                              }}
                              disabled={isProcessing}
                              test-id="make-recurring-payor-guarantor-checkbox"
                            />
                          }
                          label={`Make ${selectedRecurringPayor.name} the guarantor`}
                        />
                      </Grid>
                    );
                  })()}

                  {/* Recurring Guarantor Info Fields - show when making someone a guarantor or when existing guarantor is selected (enrollment flows only) */}
                  {/* Hidden if down payment section's make guarantor checkbox is checked */}
                  {isProgramSetup && !isDownPaymentGuarantorCheckboxChecked && (isRecurringGuarantorCheckboxChecked || payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.isGuarantor) && (
                    <Grid item xs={12}>
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Guarantor Information
                        </Typography>
                        <GuarantorAddressFields
                          addressDetails={recurringAddressDetails}
                          onAddressDetailsChange={(field, value) => setRecurringAddressDetails(prev => ({ ...prev, [field]: value }))}
                          testIdPrefix="recurring-guarantor"
                          showNameFields={isAddingNewRecurringPayor && isRecurringGuarantorCheckboxChecked}
                          nameDetails={recurringBillingDetails}
                          onNameDetailsChange={(field, value) => {
                            setRecurringBillingDetails(prev => ({ ...prev, [field]: value }));
                            if (field === 'firstName') setNewRecurringPayorFirstName(value);
                            if (field === 'lastName') setNewRecurringPayorLastName(value);
                          }}
                          isEnrollmentFlow={isProgramSetup}
                          isPatientPayor={payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.type === 'patient'}
                        />
                      </Box>
                    </Grid>
                  )}

                  <RecurringPaymentMethodSection
                    ref={recurringPaymentMethodSectionRef}
                    savedPaymentMethods={recurringUserLookupState.savedPaymentMethods}
                    billingDetails={recurringBillingDetails}
                    onBillingDetailsChange={(field: string, value: string) => {
                      setRecurringBillingDetails(prev => ({ ...prev, [field]: value }));
                      // Sync name fields for new recurring champion
                      if (isAddingNewRecurringPayor) {
                        if (field === 'firstName') setNewRecurringPayorFirstName(value);
                        if (field === 'lastName') setNewRecurringPayorLastName(value);
                      }
                    }}
                    isAddingNewChampion={isAddingNewRecurringPayor && !isRecurringGuarantorCheckboxChecked}
                    showEmailField={isAddingNewRecurringPayor || !recurringUserLookupState.userId}
                    onDeleteMethod={handleDeleteRecurringMethod}
                    deletingMethodId={deletingMethodId}
                  />
                </>
              )}
            </>
          )}

          {/* For zero down payment programs OR fully paid down payment - show payor selection, recurring payment date and method section directly */}
          {(isZeroDownPayment || isDownPaymentFullyPaid) && isProgramSetup && !isPaidInFull && !program?.isPaidInFull && (
            <>
              {/* Recurring Payor Selection for zero down payment */}
              <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Who is paying?
                </Typography>
              </Grid>
              <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
                <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
                  <Box sx={{ 
                    width: '100%', 
                    maxWidth: 400,
                    position: 'relative',
                    '& .MuiInputBase-root': { height: 56 },
                    ...(payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.isGuarantor ? {
                      '& .MuiInputBase-input': {
                        color: 'green',
                        fontWeight: 'bold',
                      },
                    } : {}),
                  }}>
                    <Tooltip title="Green indicates Guarantor" arrow>
                      <InfoOutlinedIcon 
                        sx={{ 
                          fontSize: '14px', 
                          color: 'text.secondary', 
                          cursor: 'help',
                          position: 'absolute',
                          left: '50px',
                          top: '-8px',
                          zIndex: 1,
                          backgroundColor: 'white',
                          padding: '0 2px',
                        }} 
                      />
                    </Tooltip>
                    <TextField
                      select
                      label="Name"
                      name="zeroDownPayorName"
                      value={recurringPayorId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRecurringPayorChange(e.target.value)}
                      onBlur={() => {}}
                      disabled={isProcessing}
                      test-id="zero-down-payor-dropdown"
                    >
                      {payorsWithStatus.length === 0 ? (
                        <MenuItem disabled>Loading payors...</MenuItem>
                      ) : (
                        payorsWithStatus.map((payor) => {
                          const isGuarantor = payor.isGuarantor;
                          return (
                            <MenuItem 
                              key={payor.id} 
                              value={payor.id?.toString()}
                              sx={isGuarantor ? { 
                                color: 'green', 
                                fontWeight: 'bold',
                                '&.Mui-selected': {
                                  color: 'green',
                                  fontWeight: 'bold',
                                  backgroundColor: 'rgba(0, 128, 0, 0.08)',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: 'rgba(0, 128, 0, 0.12)',
                                },
                              } : {}}
                            >
                              {payor.name}
                            </MenuItem>
                          );
                        })
                      )}
                      <Divider />
                      <MenuItem value={NEW_PAYOR_ID} sx={{ color: 'primary.main' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AddIcon fontSize="small" color="primary" />
                          <Typography variant="body2" color="primary">
                            Add new champion
                          </Typography>
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Box>
                </Box>
              </Grid>

              {/* Make zero-down payor guarantor checkbox (enrollment flows only) */}
              {isProgramSetup && recurringPayorId && (() => {
                if (recurringPayorId === NEW_PAYOR_ID) {
                  const newChampionName = newRecurringPayorFirstName && newRecurringPayorLastName 
                    ? `${newRecurringPayorFirstName} ${newRecurringPayorLastName}`.trim()
                    : 'new champion';
                  return (
                    <Grid item xs={12} sx={{ py: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isRecurringGuarantorCheckboxChecked}
                            onChange={(e) => {
                              setIsRecurringGuarantorCheckboxChecked(e.target.checked);
                              if (e.target.checked) setIsDownPaymentGuarantorCheckboxChecked(false);
                            }}
                            disabled={isProcessing}
                            test-id="make-zero-down-champion-guarantor-checkbox"
                          />
                        }
                        label={`Make ${newChampionName} the guarantor`}
                      />
                    </Grid>
                  );
                }
                
                const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === recurringPayorId);
                const isAlreadyGuarantor = selectedPayor?.isGuarantor || false;
                if (!selectedPayor || isAlreadyGuarantor) return null;
                return (
                  <Grid item xs={12} sx={{ py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isRecurringGuarantorCheckboxChecked}
                          onChange={(e) => {
                            setIsRecurringGuarantorCheckboxChecked(e.target.checked);
                            if (e.target.checked) {
                              setIsDownPaymentGuarantorCheckboxChecked(false);
                              if (selectedPayor.type === 'patient') {
                                setRecurringAddressDetails({
                                  streetAddress1: selectedPatient?.contact?.primaryAddress?.streetAddress1 || '',
                                  city: selectedPatient?.contact?.primaryAddress?.city || '',
                                  state: selectedPatient?.contact?.primaryAddress?.state?.stateCode || '',
                                  zipCode: selectedPatient?.contact?.primaryAddress?.zipCode || '',
                                  phone: Utils.formatPhoneNumber(selectedPatient?.contact?.primPhoneNum || ''),
                                  birthDate: selectedPatient?.contact?.birthDate || '',
                                  relationshipToPatient: 'Self',
                                });
                              } else {
                                const champion = selectedEncounter?.patientChampion?.find(
                                  c => c.patientChampionId === selectedPayor.id
                                );
                                if (champion) {
                                  setRecurringAddressDetails({
                                    streetAddress1: champion.contact?.primaryAddress?.streetAddress1 || '',
                                    city: champion.contact?.primaryAddress?.city || '',
                                    state: champion.contact?.primaryAddress?.state?.stateCode || '',
                                    zipCode: champion.contact?.primaryAddress?.zipCode || '',
                                    phone: Utils.formatPhoneNumber(champion.contact?.primPhoneNum || ''),
                                    birthDate: champion.contact?.birthDate || '',
                                    relationshipToPatient: champion.relationshipToPatient || '',
                                  });
                                }
                              }
                            }
                          }}
                          disabled={isProcessing}
                          test-id="make-zero-down-payor-guarantor-checkbox"
                        />
                      }
                      label={`Make ${selectedPayor.name} the guarantor`}
                    />
                  </Grid>
                );
              })()}

              <Grid item xs={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Recurring Payment Start Date
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end">
                  <DatePicker
                    error={
                      formik.touched.recurringPaymentDate &&
                      formik.errors.recurringPaymentDate
                    }
                    label="Recurring Payment Start Date"
                    minDate={getDateInFuture(1)}
                    maxDate={getDateInFuture(60)}
                    daysToDisable={[29, 30, 31]}
                    disabled={isProcessing}
                    value={formik.values.recurringPaymentDate}
                    onChange={(date: Date | null) => {
                      formik.setFieldValue('recurringPaymentDate', date ? date.toISOString() : '', false);
                    }}
                  />
                </Box>
              </Grid>
              
              {/* Recurring Guarantor Info Fields for zero down payment - show when making someone a guarantor or when existing guarantor is selected (enrollment flows only) */}
              {/* Hidden if down payment section's make guarantor checkbox is checked */}
              {isProgramSetup && !isDownPaymentGuarantorCheckboxChecked && (isRecurringGuarantorCheckboxChecked || payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.isGuarantor) && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Guarantor Information
                    </Typography>
                    <GuarantorAddressFields
                      addressDetails={recurringAddressDetails}
                      onAddressDetailsChange={(field, value) => setRecurringAddressDetails(prev => ({ ...prev, [field]: value }))}
                      testIdPrefix="zero-down-guarantor"
                      showNameFields={isAddingNewRecurringPayor && isRecurringGuarantorCheckboxChecked}
                      nameDetails={recurringBillingDetails}
                      onNameDetailsChange={(field, value) => {
                        setRecurringBillingDetails(prev => ({ ...prev, [field]: value }));
                        if (field === 'firstName') setNewRecurringPayorFirstName(value);
                        if (field === 'lastName') setNewRecurringPayorLastName(value);
                      }}
                      isEnrollmentFlow={isProgramSetup}
                      isPatientPayor={payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)?.type === 'patient'}
                    />
                  </Box>
                </Grid>
              )}

              {/* Always show recurring payment method section for zero down payment */}
              <RecurringPaymentMethodSection
                ref={recurringPaymentMethodSectionRef}
                savedPaymentMethods={recurringUserLookupState.savedPaymentMethods}
                billingDetails={recurringBillingDetails}
                onBillingDetailsChange={(field: string, value: string) => {
                  setRecurringBillingDetails(prev => ({ ...prev, [field]: value }));
                  // Sync name fields for new recurring champion
                  if (isAddingNewRecurringPayor) {
                    if (field === 'firstName') setNewRecurringPayorFirstName(value);
                    if (field === 'lastName') setNewRecurringPayorLastName(value);
                  }
                }}
                isAddingNewChampion={isAddingNewRecurringPayor && !isRecurringGuarantorCheckboxChecked}
                showEmailField={isAddingNewRecurringPayor || !recurringUserLookupState.userId}
                onDeleteMethod={handleDeleteRecurringMethod}
                deletingMethodId={deletingMethodId}
              />
            </>
          )}

          {/* Payment Reason */}
          <Grid item xs={12}>
            <TextField
              error={formik.touched.paymentReason && formik.errors.paymentReason}
              label="Payment Reason (Optional)"
              name="paymentReason"
              placeholder="Enter Payment Reason"
              value={formik.values.paymentReason}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isProcessing}
              className="w-100"
              test-id="payment-hub-reason-input"
            />
          </Grid>

          {/* Pay at Facility - hidden for program setup flows */}
          {!isMobile && !program && !isProgramSetup && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="payAtFacility"
                      checked={formik.values.payAtFacility}
                      onChange={formik.handleChange}
                      disabled={isProcessing}
                      test-id="pay-at-facility-checkbox"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Take payment at facility?</span>
                      <Tooltip title="This updates the IOC status only—it does not reduce the balance or record a transaction. Use this to indicate intent to pay at the facility. To take a payment now, use the payment section above." arrow>
                        <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', py: 0.5 }}>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Box sx={{ width: 250, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                    <TextField
                      label="Amount"
                      startAdornment={<AttachMoneyIcon />}
                      name="payAtFacilityAmount"
                      value={formik.values.payAtFacilityAmount}
                      disabled={isProcessing}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue('payAtFacilityAmount', stripCurrencyInput(e.target.value), true);
                        formik.setFieldTouched('payAtFacilityAmount', true, false);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        formik.handleBlur(e);
                        const value = parseAmount(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          formik.setFieldValue('payAtFacilityAmount', formatCurrencyInput(value), true);
                        }
                      }}
                      type="text"
                      test-id="pay-at-facility-amount-input"
                    />
                  </Box>
                  {(formik.touched.payAtFacilityAmount || formik.submitCount > 0) && formik.errors.payAtFacilityAmount && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#d32f2f', 
                        mt: '4px',
                        width: 250,
                        textAlign: 'left',
                        fontWeight: 400,
                        lineHeight: 1.66,
                        letterSpacing: '0.03333em',
                      }}
                    >
                      {formik.errors.payAtFacilityAmount}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Program Description Text for Enrollment */}
          {isProgramSetup && (() => {
            const programForText = fetchedProgram || program;
            const scheduleForText = programForText?.patientPaymentSchedule;
            if (!scheduleForText) return null;

            const isFullPay = scheduleForText.paymentFreq === 'F';

            if (isFullPay) {
              return (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                    Summary
                  </Typography>
                  <Typography variant="body2" textAlign="left" sx={{ fontSize: '2rem', lineHeight: '2.6rem' }}>
                    You have selected the <Box component="span" sx={{ fontWeight: 'bold' }}>Single Pay Program</Box>. A single
                    payment will be collected today for the full estimated amount owed. Please confirm the details above and
                    click <Box component="span" sx={{ fontWeight: 'bold' }}>Enroll</Box> to proceed.
                  </Typography>
                </Grid>
              );
            }

            const downPmtAmt = programForText.downPmtAmt || 0;
            const pfrAmt = scheduleForText.pfrAmt || 0;
            const pfrPendingBalance = scheduleForText.pfrPendingBalance || 0;
            const amountAlreadyPaid = Math.max(0, Math.min(pfrAmt - pfrPendingBalance, downPmtAmt));
            const terms = scheduleForText.terms || 0;
            const recurringAmt = scheduleForText.paymentDueAmt || 0;
            const downPmtPct = pfrAmt > 0 ? Math.round((downPmtAmt / pfrAmt) * 100) : 0;
            const isWeekly = scheduleForText.paymentFreq === 'W';
            const periodLabel = isWeekly ? 'week' : 'month';
            const periodLabelPlural = isWeekly ? 'weeks' : 'months';

            return (
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body2" textAlign="left" sx={{ fontSize: '2rem', lineHeight: '2.6rem' }}>
                  You have selected the{' '}
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {terms} {periodLabel}
                  </Box>{' '}
                  program with a down payment of {downPmtPct}%. The down payment is{' '}
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {formatNumberToUSD(downPmtAmt)}
                  </Box>
                  , of which{' '}
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {formatNumberToUSD(amountAlreadyPaid)}
                  </Box>
                  {' '}has already been paid. The remaining balance will be paid over {terms} {periodLabelPlural} at{' '}
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {formatNumberToUSD(recurringAmt)}
                  </Box>{' '}
                  per {periodLabel}. Please confirm the details above and click{' '}
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    Enroll
                  </Box>{' '}
                  to proceed.
                </Typography>
              </Grid>
            );
          })()}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                type="secondary"
                onClick={() => window.history.back()}
                disabled={isProcessing}
                test-id="payment-hub-cancel-button"
              >
                Cancel
              </Button>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }} test-id="payment-hub-submit-button">
                <Button
                  type="primary"
                  onClick={() => {
                    // Log account holder information for debugging
                    const thirdPartyGuarantor = selectedEncounter?.patientChampion?.filter(
                      (champion) => champion.isGuarantor
                    ) || [];
                    const hasExistingGuarantor = thirdPartyGuarantor.length > 0;

                    // Run pre-enroll validation BEFORE Formik validation for enrollment flows
                    if (isProgramSetup) {
                      const paymentAmount = parseAmount(formik.values.amount) || 0;
                      const programForValidation = fetchedProgram || program;
                      const pendingBalance = programForValidation?.patientPaymentSchedule?.pfrPendingBalance;

                      const isCreatingNewGuarantor = isDownPaymentGuarantorCheckboxChecked || isRecurringGuarantorCheckboxChecked;

                      // Determine which guarantor form is visible (same logic as onSubmit)
                      const selectedDownPaymentPayor = payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName);
                      const selectedRecurringPayor = payorsWithStatus.find(p => p.id?.toString() === recurringPayorId);
                      
                      const isDownPaymentGuarantorFormVisible = !isZeroDownPayment && !isDownPaymentFullyPaid &&
                        ((isProgramSetup && (formik.values.useForRecurring || isFullPayProgram)) || !isProgramSetup) &&
                        !isRecurringGuarantorCheckboxChecked && (isDownPaymentGuarantorCheckboxChecked || selectedDownPaymentPayor?.isGuarantor);
                      const isRecurringGuarantorFormVisible = !isDownPaymentGuarantorCheckboxChecked && 
                        (isRecurringGuarantorCheckboxChecked || selectedRecurringPayor?.isGuarantor);

                      // Use form data from whichever guarantor form is currently visible
                      const visibleFormData = isDownPaymentGuarantorFormVisible ? addressDetails
                        : (isRecurringGuarantorFormVisible ? recurringAddressDetails : null);

                      const guarantorFormData = visibleFormData ? {
                        phone: visibleFormData.phone,
                        birthDate: visibleFormData.birthDate,
                        relationshipToPatient: visibleFormData.relationshipToPatient,
                        address: {
                          streetAddress1: visibleFormData.streetAddress1,
                          city: visibleFormData.city,
                          state: visibleFormData.state,
                          zipCode: visibleFormData.zipCode,
                        },
                      } : undefined;

                      // Determine account holder name based on who is being made the guarantor
                      let accountHolderName: string | undefined;
                      if (isCreatingNewGuarantor) {
                        // Check if it's a new payor being created or an existing payor being made guarantor
                        const isNewDownPaymentPayor = isDownPaymentGuarantorCheckboxChecked && formik.values.payorName === NEW_PAYOR_ID;
                        const isNewRecurringPayor = isRecurringGuarantorCheckboxChecked && recurringPayorId === NEW_PAYOR_ID;
                        
                        if (isNewDownPaymentPayor) {
                          accountHolderName = `${newPayorFirstName} ${newPayorLastName}`.trim() || 'New Guarantor';
                        } else if (isNewRecurringPayor) {
                          accountHolderName = `${newRecurringPayorFirstName} ${newRecurringPayorLastName}`.trim() || 'New Guarantor';
                        } else {
                          // Existing payor being made guarantor - get their name from payorsWithStatus
                          const selectedPayorId = isDownPaymentGuarantorCheckboxChecked ? formik.values.payorName : recurringPayorId;
                          const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === selectedPayorId);
                          accountHolderName = selectedPayor?.name || 'Account Holder';
                        }
                      } else if (hasExistingGuarantor) {
                        accountHolderName = `${thirdPartyGuarantor[0]?.contact?.firstName || ''} ${thirdPartyGuarantor[0]?.contact?.lastName || ''}`.trim();
                      } else {
                        accountHolderName = `${selectedPatient?.contact?.firstName || ''} ${selectedPatient?.contact?.lastName || ''}`.trim();
                      }

                      const useForRecurringAppliesClick = formik.values.useForRecurring && !isZeroDownPayment && !isDownPaymentFullyPaid;
                      const hasRecurringMethodClick = useForRecurringAppliesClick
                        || !!(recurringPaymentMethodSectionRef.current?.getSelectedMethodId())
                        || !!(recurringPaymentMethodSectionRef.current?.isAddingNew());

                      const needsDownPaymentACHClick = paymentSource === 'ACH' && !isZeroDownPayment && !isDownPaymentFullyPaid;
                      const isDownPaymentACHValidClick = !needsDownPaymentACHClick || !achPaymentRef.current || achPaymentRef.current.validate();

                      const recurringRefClick = recurringPaymentMethodSectionRef.current;
                      const needsRecurringACHClick = recurringRefClick?.isAddingNew() && recurringRefClick?.getPaymentSource() === 'ACH';
                      const isRecurringACHValidClick = !needsRecurringACHClick || !recurringRefClick?.getAchFormRef() || recurringRefClick.getAchFormRef()!.validate();

                      const isACHValidClick = isDownPaymentACHValidClick && isRecurringACHValidClick;

                      const validationResult = validatePreEnrollRequirements({
                        encounter: selectedEncounter,
                        patient: selectedPatient,
                        program: programForValidation,
                        isDownPaymentFullyPaid,
                        isLoading: false,
                        hasRecurringMethod: hasRecurringMethodClick,
                        currentPaymentAmount: paymentAmount,
                        pendingRemainingBalance: pendingBalance,
                        recurringPaymentDate: formik.values.recurringPaymentDate,
                        isCreatingNewGuarantor,
                        newGuarantorData: isCreatingNewGuarantor ? guarantorFormData : undefined,
                        existingGuarantorFormData: !isCreatingNewGuarantor ? guarantorFormData : undefined,
                        accountHolderName,
                        patientId: patientId ? parseInt(patientId, 10) : undefined,
                        encounterId: patientEncounterId ? parseInt(patientEncounterId, 10) : undefined,
                        billingEmail: (isZeroDownPayment || isDownPaymentFullyPaid) ? recurringBillingDetails.email : billingDetails.email,
                        isPaymentMethodValid: isACHValidClick,
                      });

                      console.log('[PaymentHub] Pre-enroll validation (button click):', {
                        isValid: validationResult.isValid,
                        failedRequirements: validationResult.failedRequirements.map(r => r.requirementName),
                        isCreatingNewGuarantor,
                        isDownPaymentGuarantorFormVisible,
                        isRecurringGuarantorFormVisible,
                        guarantorFormData,
                        accountHolderName,
                      });

                      if (!validationResult.isValid) {
                        setPreEnrollFailedRequirements(validationResult.failedRequirements);
                        setShowPreEnrollErrorModal(true);
                        return; // Don't proceed with Formik submission
                      }
                    } else {
                      // Run pre-pay validation for non-enrollment flows
                      // Pass guarantor form data if the guarantor form is visible
                      // This includes: existing guarantors OR when "make guarantor" checkbox is checked
                      const selectedPayorForValidation = payorsWithStatus.find(p => p.id?.toString() === formik.values.payorName);
                      const isGuarantorFormVisible = selectedPayorForValidation?.isGuarantor || isDownPaymentGuarantorCheckboxChecked;
                      const guarantorFormDataForValidation = isGuarantorFormVisible ? {
                        birthDate: addressDetails.birthDate,
                      } : undefined;

                      const prePayValidationResult = validatePrePayRequirements({
                        patient: selectedPatient,
                        patientId: patientId ? parseInt(patientId, 10) : undefined,
                        billingEmail: billingDetails.email,
                        guarantorFormData: guarantorFormDataForValidation,
                        isProgramSetup,
                        downPaymentChampion: {
                          isNewChampion: formik.values.payorName === NEW_PAYOR_ID,
                          isGuarantor: isDownPaymentGuarantorCheckboxChecked,
                          addressDetails,
                          label: 'down payment champion',
                        },
                        recurringChampion: {
                          isNewChampion: recurringPayorId === NEW_PAYOR_ID,
                          isGuarantor: isRecurringGuarantorCheckboxChecked,
                          addressDetails: recurringAddressDetails,
                          label: 'recurring payment champion',
                        },
                      });

                      console.log('[PaymentHub] Pre-pay validation (button click):', {
                        isValid: prePayValidationResult.isValid,
                        failedRequirements: prePayValidationResult.failedRequirements.map(r => r.requirementName),
                      });

                      if (!prePayValidationResult.isValid) {
                        setPrePayFailedRequirements(prePayValidationResult.failedRequirements);
                        setShowPrePayErrorModal(true);
                        return; // Don't proceed with Formik submission
                      }
                    }
                    
                    formik.handleSubmit();
                  }}
                >
                  {(() => {
                    const paymentAmt = parseAmount(formik.values.amount);
                    const facilityAmt = formik.values.payAtFacility && formik.values.payAtFacilityAmount 
                      ? parseAmount(formik.values.payAtFacilityAmount) 
                      : 0;
                    
                    const hasPayment = paymentAmt > 0;
                    const hasFacility = facilityAmt > 0;
                    const isCashOrCheck = paymentSource === 'CASH' || paymentSource === 'CHECK';
                    
                    // Enrollment-only: down payment fully paid or zero down payment
                    const isEnrollOnly = isProgramSetup && (isDownPaymentFullyPaid || isZeroDownPayment) && !hasPayment;
                    
                    // Build button text based on what's being done
                    if (isEnrollOnly) {
                      // Enrollment only - no payment required
                      return 'Enroll';
                    } else if (hasPayment && hasFacility) {
                      // Both payment and facility record
                      const prefix = isProgramSetup ? 'Enroll & ' : '';
                      return `${prefix}Pay ${formatNumberToUSD(paymentAmt)} and Record ${formatNumberToUSD(facilityAmt)}`;
                    } else if (hasPayment) {
                      // Payment only
                      const prefix = isProgramSetup ? 'Enroll & Pay' : 'Pay';
                      return `${prefix} ${formatNumberToUSD(paymentAmt)}`;
                    } else if (hasFacility && isCashOrCheck) {
                      // Facility-only (record) - only valid for Cash/Check
                      return `Record ${formatNumberToUSD(facilityAmt)}`;
                    } else {
                      // No amounts entered or Card/ACH with facility-only (not supported)
                      return isProgramSetup ? 'Enroll & Pay' : 'Pay';
                    }
                  })()}
                </Button>
                {isProcessing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(25, 118, 210, 0.9)',
                      borderRadius: '4px',
                    }}
                  >
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          </Grid>
        </Paper>
      </div>

      <DeletePaymentMethodWarningModal
        isOpen={showDeleteWarningModal}
        onCancel={() => {
          setShowDeleteWarningModal(false);
          setWarningPendingDeleteMethodId(null);
        }}
        onConfirm={handleConfirmWarningDelete}
      />

      {/* Recurring Payment Method Selection Modal */}
      <Dialog
        open={showRecurringMethodModal}
        onClose={() => {
          setShowRecurringMethodModal(false);
          setPendingDeleteMethodId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Select New Recurring Payment Method
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This payment method is used for recurring payments. Please select another payment method to use for recurring payments before removing it.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            value={recurringPaymentMethodId?.toString() || ''}
            onChange={(e) => {
              const newMethodId = parseInt(e.target.value, 10);
              setRecurringPaymentMethodId(newMethodId);
            }}
          >
            {filterCardMethods(userLookupState.savedPaymentMethods)
              .filter(m => m.userPaymentMethodId !== pendingDeleteMethodId)
              .map((method) => {
                const methodId = method.userPaymentMethodId?.toString() || '';
                return (
                  <Paper
                    key={methodId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: recurringPaymentMethodId?.toString() === methodId ? '#e3f2fd' : 'white',
                      borderColor: recurringPaymentMethodId?.toString() === methodId ? 'primary.main' : 'divider',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={() => setRecurringPaymentMethodId(method.userPaymentMethodId!)}
                  >
                    <FormControlLabel
                      value={methodId}
                      control={<Radio size="small" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CreditCardIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formatPaymentMethodDisplay(method)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Expires {formatExpiration(method)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Paper>
                );
              })}
          </RadioGroup>
          {filterCardMethods(userLookupState.savedPaymentMethods).filter(m => m.userPaymentMethodId !== pendingDeleteMethodId).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No other payment methods available. Please add a new payment method first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="secondary"
            onClick={() => {
              setShowRecurringMethodModal(false);
              setPendingDeleteMethodId(null);
            }}
            test-id="recurring-method-modal-cancel"
          >
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              type="primary"
              disabled={
                !recurringPaymentMethodId || 
                recurringPaymentMethodId === pendingDeleteMethodId ||
                deletingMethodId !== null
              }
              test-id="recurring-method-modal-confirm"
              onClick={async () => {
                if (!userLookupState.userId || !pendingDeleteMethodId) return;

                setDeletingMethodId(pendingDeleteMethodId);
                try {
                  const result = await deleteAndRefetchPaymentMethods({
                    userId: parseInt(userLookupState.userId, 10),
                    userPaymentMethodId: pendingDeleteMethodId,
                    authToken,
                  });

                  if (result.success) {
                    setUserLookupState(prev => ({ ...prev, savedPaymentMethods: result.paymentMethods }));
                    if (selectedSavedMethod === pendingDeleteMethodId.toString()) {
                      setSelectedSavedMethod(null);
                    }
                    setShowRecurringMethodModal(false);
                    setPendingDeleteMethodId(null);
                  } else {
                    dispatch(showErrorStatus(result.message));
                  }
                } finally {
                  setDeletingMethodId(null);
                }
              }}
            >
              Confirm & Remove
            </Button>
            {deletingMethodId && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-10px',
                  marginLeft: '-10px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Pre-Enroll Validation Error Modal */}
      <PreEnrollErrorModal
        isOpen={showPreEnrollErrorModal}
        handleModalClose={() => setShowPreEnrollErrorModal(false)}
        requirementList={preEnrollFailedRequirements}
        onFixClick={(url) => {
          setShowPreEnrollErrorModal(false);
          navigate(url);
        }}
      />

      {/* Pre-Pay Validation Error Modal (for non-enroll flows) */}
      <PrePayErrorModal
        isOpen={showPrePayErrorModal}
        handleModalClose={() => setShowPrePayErrorModal(false)}
        requirementList={prePayFailedRequirements}
        onFixClick={(url) => {
          setShowPrePayErrorModal(false);
          navigate(url);
        }}
      />
    </main>
  );
};

export default PaymentHubView;
